import React from "react";
import { createPortal } from "react-dom";
import { ApolloError } from "@apollo/client";
import {
  FiCheck,
  FiChevronDown,
  FiImage,
  FiMaximize2,
  FiRefreshCw,
  FiRotateCcw,
  FiRotateCw,
  FiUploadCloud,
  FiZoomIn,
  FiZoomOut,
} from "react-icons/fi";
import { HiSparkles } from "react-icons/hi2";
import { IoClose } from "react-icons/io5";

import { applyFloorplanImportDraft } from "../../features/floorplanImageImport/applyFloorplanImportDraft";
import {
  resolveFloorplanImportReviewSelection,
} from "../../features/floorplanImageImport/floorplanImportDefaults";
import {
  getImportedRectangleFallback,
  isFloorplanImportSymbolInsertable,
  resolveImportedCatalogRotation,
} from "../../features/floorplanImageImport/importedSymbolGeometry";
import {
  FloorplanImageImportFieldsFragment,
  FloorplanImageImportMode,
  FloorplanImageImportStage,
  FloorplanImageImportStatus,
  FloorplanImportScaleSource,
  FloorplanImportStageStatus,
  useCancelFloorplanImageImportMutation,
  useFloorplanImageImportCreditCostQuery,
  useFloorplanImageImportQuery,
  useMarkFloorplanImageImportAppliedMutation,
  useProvideFloorplanImageImportCalibrationMutation,
  useRetryFloorplanImageImportMutation,
  useRetryFloorplanImageImportStageMutation,
  useStartFloorplanImageImportMutation,
  useUpdateFloorplanImageImportReviewMutation,
} from "../../gql/graphql";
import { floorplannerStore } from "../../store/floorplannerStore";
import { resolveUserModel3DUrl } from "../../features/floorplan3d/userModels/userModel3DUrls";
import { getFloorplanImportSymbolCatalog } from "./SymbolLibraryTabs";
import {
  degreesToRadians,
  getReviewSelectionState,
  normalizeDegrees,
  radiansToDegrees,
  setReviewRefsIncluded,
  toggleReviewRef,
} from "../../features/floorplanImageImport/floorplanImportReview";
import {
  calculateWheelZoom,
  MAX_IMAGE_ZOOM,
  MIN_IMAGE_ZOOM,
  stepImageZoom,
} from "../../features/floorplanImageImport/imageZoom";

/**
 * Temporary: keep the review → insert flow, but hide zoom chrome, scale
 * adjust, item toggles, and numbered markers until those controls are more
 * useful. Flip to `true` to restore them without rewriting the UI.
 */
const FLOORPLAN_IMPORT_REVIEW_EXTRA_CONTROLS_ENABLED = false;

type Props = {
  isOpen: boolean;
  importId?: string;
  onClose: () => void;
  onChanged?: () => void;
};

type NormalizedPoint = { x: number; y: number };

/** Default length filled in once both reference points are placed by click. */
const DEFAULT_REFERENCE_DISTANCE_MM = "900";

type CandidateKind =
  | "wall"
  | "line"
  | "opening"
  | "symbol"
  | "label"
  | "measurement";
type ReviewRoot = "structure" | "symbols" | "annotations";
type DraftCandidate = {
  ref: string;
  label: string;
  kind: CandidateKind;
  insertable: boolean;
  root: ReviewRoot;
  subgroup: string;
};

const MAX_IMAGE_BYTES = 20 * 1024 * 1024;
const ACCEPTED_IMAGE_TYPES = ["image/jpeg", "image/png", "image/webp"];
const candidateColors: Record<CandidateKind, string> = {
  wall: "#2563eb",
  line: "#0f766e",
  opening: "#7c3aed",
  symbol: "#059669",
  label: "#0891b2",
  measurement: "#06b6d4",
};

const activeStatuses = new Set<FloorplanImageImportStatus>([
  FloorplanImageImportStatus.Queued,
  FloorplanImageImportStatus.Processing,
  FloorplanImageImportStatus.NeedsInput,
  FloorplanImageImportStatus.Cancelling,
]);

const isReviewableStatus = (status?: FloorplanImageImportStatus) =>
  status === FloorplanImageImportStatus.Ready ||
  status === FloorplanImageImportStatus.Applied;

const MARK_APPLIED_AFTER_INSERT_ERROR =
  "The drawing is in the plan, but the import could not be marked as saved. You can close this dialog — the drawing is already inserted.";

const reviewPersistenceSignature = ({
  draftRevision,
  excludedRefs,
  symbolOverrides,
}: {
  draftRevision?: number | null;
  excludedRefs: ReadonlySet<string>;
  symbolOverrides: ReadonlyMap<
    string,
    { libraryKey: string; rotation: number }
  >;
}) =>
  JSON.stringify({
    draftRevision,
    excludedEntityIds: [...excludedRefs].sort(),
    symbolOverrides: [...symbolOverrides.entries()]
      .map(([ref, override]) => ({ ref, ...override }))
      .sort((a, b) => a.ref.localeCompare(b.ref)),
  });

const stageLabels: Array<[FloorplanImageImportStage, string]> = [
  [FloorplanImageImportStage.Normalizing, "Preparing image"],
  [FloorplanImageImportStage.Layering, "Separating drawing layers"],
  [FloorplanImageImportStage.TracingStructure, "Tracing walls"],
  [FloorplanImageImportStage.ReconcilingStructure, "Connecting wall geometry"],
  [FloorplanImageImportStage.DetectingOpenings, "Attaching doors and windows"],
  // Keep DetectingSymbols mapping for stageLabel(), but omit from the visible
  // progress list while server-side symbol scanning is disabled.
  [
    FloorplanImageImportStage.ReadingAnnotations,
    "Reading labels and dimensions",
  ],
  [FloorplanImageImportStage.Verifying, "Checking the reconstructed plan"],
  [FloorplanImageImportStage.Finalizing, "Preparing your review"],
];

/** Hidden while symbol scanning is disabled; still used for stageLabel lookup. */
const disabledStageLabels: Array<[FloorplanImageImportStage, string]> = [
  [FloorplanImageImportStage.DetectingSymbols, "Finding symbols"],
];

const legacyStageLabels: Array<[FloorplanImageImportStage, string]> = [
  [FloorplanImageImportStage.Normalizing, "Preparing image"],
  [FloorplanImageImportStage.Understanding, "Detecting structures and symbols"],
  [FloorplanImageImportStage.Reconciling, "Building measured geometry"],
  [FloorplanImageImportStage.Validating, "Checking the editable draft"],
];

const stageLabel = (stage: FloorplanImageImportStage) =>
  [...stageLabels, ...disabledStageLabels, ...legacyStageLabels].find(
    ([value]) => value === stage,
  )?.[1] || "Waiting to start";

const rootLabels: Record<ReviewRoot, string> = {
  structure: "Structure",
  symbols: "Symbols",
  annotations: "Annotations",
};

const getErrorMessage = (error: unknown) => {
  if (error instanceof ApolloError) {
    const limitError = error.graphQLErrors.some(
      (item) => item.extensions?.code === "AI_CREDIT_LIMIT_REACHED",
    );
    if (limitError) return "You do not have enough AI credits for this import.";
    const revisionError = error.graphQLErrors.some(
      (item) => item.extensions?.code === "FLOORPLAN_IMPORT_REVISION_CONFLICT",
    );
    if (revisionError) {
      return "The detected draft changed. It has been refreshed; repeat your last edit.";
    }
  }
  if (error instanceof Error)
    return "We couldn’t complete that check. Please try again.";
  return "Something went wrong. Please try again.";
};

/** Zoom/pan for source review and reference-scale placement. */
const TRACE_MODAL_ZOOM_ENABLED = true;

const ImageZoomControls: React.FC<{
  zoom: number;
  onChange: (zoom: number) => void;
}> = ({ zoom, onChange }) => {
  const buttonClass =
    "flex h-8 w-8 items-center justify-center text-[#171713] transition-colors hover:bg-[#eeece5] focus:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-black disabled:cursor-not-allowed disabled:text-[#aaa69d]";
  return (
    <div
      className="flex items-center overflow-hidden rounded-lg border border-[#c9c5ba] bg-white shadow-sm"
      aria-label="Image zoom controls"
    >
      <button
        type="button"
        aria-label="Zoom out"
        disabled={zoom <= MIN_IMAGE_ZOOM}
        onClick={() => onChange(stepImageZoom(zoom, -1))}
        className={buttonClass}
      >
        <FiZoomOut size={15} />
      </button>
      <span className="min-w-12 border-x border-[#dedbd2] px-2 text-center text-[11px] font-semibold tabular-nums text-[#555149]">
        {Math.round(zoom * 100)}%
      </span>
      <button
        type="button"
        aria-label="Zoom in"
        disabled={zoom >= MAX_IMAGE_ZOOM}
        onClick={() => onChange(stepImageZoom(zoom, 1))}
        className={buttonClass}
      >
        <FiZoomIn size={15} />
      </button>
      <button
        type="button"
        aria-label="Fit image"
        disabled={zoom === MIN_IMAGE_ZOOM}
        onClick={() => onChange(MIN_IMAGE_ZOOM)}
        className="flex h-8 w-8 items-center justify-center border-l border-[#dedbd2] text-[#171713] transition-colors hover:bg-[#eeece5] focus:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-black disabled:cursor-not-allowed disabled:text-[#aaa69d]"
      >
        <FiMaximize2 size={14} />
      </button>
    </div>
  );
};

const ZoomableImageCanvas: React.FC<{
  sourceUrl: string;
  alt: string;
  zoom: number;
  onZoomChange: (zoom: number) => void;
  zoomEnabled?: boolean;
  viewportClassName: string;
  viewportRef?: React.MutableRefObject<HTMLDivElement | null>;
  onImageClick?: (event: React.MouseEvent<HTMLImageElement>) => void;
  onImageMouseMove?: (event: React.MouseEvent<HTMLImageElement>) => void;
  onImageMouseLeave?: () => void;
  children?: React.ReactNode;
}> = ({
  sourceUrl,
  alt,
  zoom,
  onZoomChange,
  zoomEnabled = true,
  viewportClassName,
  viewportRef,
  onImageClick,
  onImageMouseMove,
  onImageMouseLeave,
  children,
}) => {
  const localViewportRef = React.useRef<HTMLDivElement | null>(null);
  const previousMetricsRef = React.useRef<
    | {
        imageLeft: number;
        imageTop: number;
        imageWidth: number;
        imageHeight: number;
      }
    | undefined
  >(undefined);
  const zoomAnchorRef = React.useRef<
    | {
        imageX: number;
        imageY: number;
        viewportX: number;
        viewportY: number;
      }
    | undefined
  >(undefined);
  const dragRef = React.useRef<
    | {
        pointerId: number;
        clientX: number;
        clientY: number;
        scrollLeft: number;
        scrollTop: number;
        moved: boolean;
      }
    | undefined
  >(undefined);
  const suppressClickRef = React.useRef(false);
  const suppressClickTimerRef = React.useRef<number | undefined>(undefined);
  const zoomRef = React.useRef(zoom);
  zoomRef.current = zoom;
  const [isDragging, setIsDragging] = React.useState(false);
  const [naturalSize, setNaturalSize] = React.useState({
    sourceUrl: "",
    width: 0,
    height: 0,
  });
  const [viewportSize, setViewportSize] = React.useState({
    width: 0,
    height: 0,
  });

  const setViewportNode = React.useCallback(
    (node: HTMLDivElement | null) => {
      localViewportRef.current = node;
      if (viewportRef) viewportRef.current = node;
    },
    [viewportRef],
  );

  React.useEffect(() => {
    const viewport = localViewportRef.current;
    if (!viewport) return;
    const updateSize = () =>
      setViewportSize({
        width: viewport.clientWidth,
        height: viewport.clientHeight,
      });
    updateSize();
    if (typeof ResizeObserver === "undefined") {
      window.addEventListener("resize", updateSize);
      return () => window.removeEventListener("resize", updateSize);
    }
    const observer = new ResizeObserver(updateSize);
    observer.observe(viewport);
    return () => observer.disconnect();
  }, []);

  const padding = 16;
  const availableWidth = Math.max(1, viewportSize.width - padding);
  const availableHeight = Math.max(1, viewportSize.height - padding);
  const hasNaturalSize =
    naturalSize.sourceUrl === sourceUrl &&
    naturalSize.width > 0 &&
    naturalSize.height > 0;
  const fitScale = hasNaturalSize
    ? Math.min(
        availableWidth / naturalSize.width,
        availableHeight / naturalSize.height,
      )
    : 0;
  const imageWidth = Math.max(
    1,
    hasNaturalSize ? naturalSize.width * fitScale * zoom : 0,
  );
  const imageHeight = Math.max(
    1,
    hasNaturalSize ? naturalSize.height * fitScale * zoom : 0,
  );
  const canvasWidth = Math.max(viewportSize.width, imageWidth + padding);
  const canvasHeight = Math.max(viewportSize.height, imageHeight + padding);
  const imageLeft = (canvasWidth - imageWidth) / 2;
  const imageTop = (canvasHeight - imageHeight) / 2;

  React.useEffect(() => {
    const viewport = localViewportRef.current;
    if (!viewport || !hasNaturalSize) {
      previousMetricsRef.current = undefined;
      return;
    }
    const previousMetrics = previousMetricsRef.current;
    const requestedAnchor = zoomAnchorRef.current;
    const viewportX = requestedAnchor?.viewportX ?? viewport.clientWidth / 2;
    const viewportY = requestedAnchor?.viewportY ?? viewport.clientHeight / 2;
    const imageX =
      requestedAnchor?.imageX ??
      (previousMetrics
        ? (viewport.scrollLeft + viewportX - previousMetrics.imageLeft) /
          previousMetrics.imageWidth
        : 0.5);
    const imageY =
      requestedAnchor?.imageY ??
      (previousMetrics
        ? (viewport.scrollTop + viewportY - previousMetrics.imageTop) /
          previousMetrics.imageHeight
        : 0.5);
    const frame = requestAnimationFrame(() => {
      viewport.scrollTo({
        left: Math.max(0, imageLeft + imageX * imageWidth - viewportX),
        top: Math.max(0, imageTop + imageY * imageHeight - viewportY),
        behavior: "auto",
      });
    });
    previousMetricsRef.current = {
      imageLeft,
      imageTop,
      imageWidth,
      imageHeight,
    };
    zoomAnchorRef.current = undefined;
    return () => cancelAnimationFrame(frame);
  }, [
    canvasHeight,
    canvasWidth,
    hasNaturalSize,
    imageHeight,
    imageLeft,
    imageTop,
    imageWidth,
    naturalSize.height,
    naturalSize.width,
    zoom,
  ]);

  React.useEffect(() => {
    const viewport = localViewportRef.current;
    if (!viewport || !zoomEnabled) return;
    const handleWheel = (event: WheelEvent) => {
      if (event.deltaY === 0) return;
      event.preventDefault();
      const currentZoom = zoomRef.current;
      const nextZoom = calculateWheelZoom({
        zoom: currentZoom,
        deltaY: event.deltaY,
        deltaMode: event.deltaMode,
        viewportHeight: viewport.clientHeight,
      });
      if (nextZoom === currentZoom) return;
      const metrics = previousMetricsRef.current;
      const viewportRect = viewport.getBoundingClientRect();
      const viewportX = event.clientX - viewportRect.left;
      const viewportY = event.clientY - viewportRect.top;
      if (metrics) {
        zoomAnchorRef.current = {
          imageX: Math.min(
            1,
            Math.max(
              0,
              (viewport.scrollLeft + viewportX - metrics.imageLeft) /
                metrics.imageWidth,
            ),
          ),
          imageY: Math.min(
            1,
            Math.max(
              0,
              (viewport.scrollTop + viewportY - metrics.imageTop) /
                metrics.imageHeight,
            ),
          ),
          viewportX,
          viewportY,
        };
      }
      zoomRef.current = nextZoom;
      onZoomChange(nextZoom);
    };
    viewport.addEventListener("wheel", handleWheel, { passive: false });
    return () => viewport.removeEventListener("wheel", handleWheel);
  }, [onZoomChange, zoomEnabled]);

  React.useEffect(
    () => () => {
      if (suppressClickTimerRef.current !== undefined) {
        window.clearTimeout(suppressClickTimerRef.current);
      }
    },
    [],
  );

  const isCalibrating = Boolean(onImageClick);
  /**
   * While placing a reference measurement, left-click must set points — not pan.
   * Pan with middle mouse, or Alt + left-drag, when zoomed in.
   */
  const startPan = (event: React.PointerEvent<HTMLDivElement>) => {
    if (!zoomEnabled || zoom <= MIN_IMAGE_ZOOM) return;
    const middleButton = event.button === 1;
    const altLeftDrag = event.button === 0 && event.altKey;
    const freeLeftDrag = event.button === 0 && !isCalibrating;
    if (!middleButton && !altLeftDrag && !freeLeftDrag) return;
    if (middleButton) event.preventDefault();
    const viewport = event.currentTarget;
    dragRef.current = {
      pointerId: event.pointerId,
      clientX: event.clientX,
      clientY: event.clientY,
      scrollLeft: viewport.scrollLeft,
      scrollTop: viewport.scrollTop,
      moved: false,
    };
    setIsDragging(true);
  };

  const pan = (event: React.PointerEvent<HTMLDivElement>) => {
    const drag = dragRef.current;
    if (!drag || drag.pointerId !== event.pointerId) return;
    const deltaX = event.clientX - drag.clientX;
    const deltaY = event.clientY - drag.clientY;
    if (Math.hypot(deltaX, deltaY) > 3 && !drag.moved) {
      drag.moved = true;
      event.currentTarget.setPointerCapture(event.pointerId);
    }
    event.currentTarget.scrollLeft = drag.scrollLeft - deltaX;
    event.currentTarget.scrollTop = drag.scrollTop - deltaY;
    event.preventDefault();
  };

  const stopPan = (event: React.PointerEvent<HTMLDivElement>) => {
    const drag = dragRef.current;
    if (!drag || drag.pointerId !== event.pointerId) return;
    if (event.currentTarget.hasPointerCapture(event.pointerId)) {
      event.currentTarget.releasePointerCapture(event.pointerId);
    }
    if (drag.moved) {
      suppressClickRef.current = true;
      if (suppressClickTimerRef.current !== undefined) {
        window.clearTimeout(suppressClickTimerRef.current);
      }
      suppressClickTimerRef.current = window.setTimeout(() => {
        suppressClickRef.current = false;
      }, 0);
    }
    dragRef.current = undefined;
    setIsDragging(false);
  };

  const handleImageClick = (event: React.MouseEvent<HTMLImageElement>) => {
    if (suppressClickRef.current) {
      suppressClickRef.current = false;
      event.preventDefault();
      return;
    }
    onImageClick?.(event);
  };

  return (
    <div
      ref={setViewportNode}
      role="region"
      aria-label={
        zoomEnabled
          ? isCalibrating
            ? "Zoomable image. Wheel to zoom. Click to mark points. Alt-drag or middle-mouse to pan."
            : "Zoomable image. Use the mouse wheel to zoom and drag to pan."
          : "Imported drawing preview"
      }
      tabIndex={0}
      onPointerDown={startPan}
      onPointerMove={pan}
      onPointerUp={stopPan}
      onPointerCancel={stopPan}
      className={`${viewportClassName} overscroll-contain focus:outline-none focus-visible:ring-2 focus-visible:ring-black ${
        isCalibrating
          ? isDragging
            ? "cursor-grabbing touch-none"
            : "cursor-crosshair touch-none"
          : zoomEnabled && zoom > MIN_IMAGE_ZOOM
            ? isDragging
              ? "cursor-grabbing touch-none"
              : "cursor-grab touch-none"
            : ""
      }`}
    >
      <div
        className="relative"
        style={{ width: canvasWidth, height: canvasHeight }}
      >
        <div
          className="absolute leading-none"
          style={{
            left: imageLeft,
            top: imageTop,
            width: imageWidth,
            height: imageHeight,
          }}
        >
          <img
            src={sourceUrl}
            alt={alt}
            draggable={false}
            onClick={handleImageClick}
            onMouseMove={onImageMouseMove}
            onMouseLeave={onImageMouseLeave}
            onLoad={(event) =>
              setNaturalSize({
                sourceUrl,
                width: event.currentTarget.naturalWidth,
                height: event.currentTarget.naturalHeight,
              })
            }
            className="block h-full w-full select-none"
          />
          {children}
        </div>
      </div>
    </div>
  );
};

/**
 * Snap calibration rubber-band to 45° increments (same as backdrop measure).
 * Hold Shift to move freely without snapping.
 */
const snapCalibrationPointTo45 = (
  start: NormalizedPoint,
  current: NormalizedPoint,
  imageWidth: number,
  imageHeight: number,
): NormalizedPoint => {
  const startPx = { x: start.x * imageWidth, y: start.y * imageHeight };
  const currentPx = { x: current.x * imageWidth, y: current.y * imageHeight };
  const dx = currentPx.x - startPx.x;
  const dy = currentPx.y - startPx.y;
  const length = Math.hypot(dx, dy);
  if (length < 1) return current;
  const angle = Math.atan2(dy, dx);
  const snappedAngle = Math.round(angle / (Math.PI / 4)) * (Math.PI / 4);
  const snappedPx = {
    x: startPx.x + length * Math.cos(snappedAngle),
    y: startPx.y + length * Math.sin(snappedAngle),
  };
  return {
    x: Math.min(1, Math.max(0, snappedPx.x / imageWidth)),
    y: Math.min(1, Math.max(0, snappedPx.y / imageHeight)),
  };
};

const CalibrationOverlay: React.FC<{
  points: NormalizedPoint[];
  previewPoint?: NormalizedPoint | null;
  distanceMm: string;
  onDistanceChange: (value: string) => void;
  onPointsChange: (points: NormalizedPoint[]) => void;
}> = ({
  points,
  previewPoint,
  distanceMm,
  onDistanceChange,
  onPointsChange,
}) => {
  const containerRef = React.useRef<HTMLDivElement>(null);
  const inputRef = React.useRef<HTMLInputElement>(null);
  const [size, setSize] = React.useState({ width: 1, height: 1 });
  const [isEditingLabel, setIsEditingLabel] = React.useState(true);
  const dragIndexRef = React.useRef<number | null>(null);
  const hadBothPointsRef = React.useRef(false);

  React.useLayoutEffect(() => {
    const node = containerRef.current;
    if (!node) return;
    const update = () =>
      setSize({ width: node.clientWidth || 1, height: node.clientHeight || 1 });
    update();
    const observer = new ResizeObserver(update);
    observer.observe(node);
    return () => observer.disconnect();
  }, []);

  React.useEffect(() => {
    const bothPoints = points.length === 2;
    if (bothPoints && !hadBothPointsRef.current) {
      setIsEditingLabel(true);
    }
    if (!bothPoints) {
      setIsEditingLabel(true);
    }
    hadBothPointsRef.current = bothPoints;
  }, [points.length]);

  React.useEffect(() => {
    if (!isEditingLabel || points.length !== 2) return;
    const frame = window.requestAnimationFrame(() => {
      const input = inputRef.current;
      if (!input) return;
      input.focus({ preventScroll: true });
      input.select();
    });
    return () => window.cancelAnimationFrame(frame);
  }, [isEditingLabel, points.length]);

  React.useEffect(() => {
    const onMove = (event: PointerEvent) => {
      const index = dragIndexRef.current;
      const node = containerRef.current;
      if (index === null || !node) return;
      const rect = node.getBoundingClientRect();
      let next = {
        x: Math.min(1, Math.max(0, (event.clientX - rect.left) / rect.width)),
        y: Math.min(1, Math.max(0, (event.clientY - rect.top) / rect.height)),
      };
      // Snap the free end to 45° unless Shift is held (matches backdrop measure).
      if (
        index === 1 &&
        points[0] &&
        !event.shiftKey &&
        rect.width > 0 &&
        rect.height > 0
      ) {
        next = snapCalibrationPointTo45(
          points[0],
          next,
          rect.width,
          rect.height,
        );
      }
      onPointsChange(
        points.map((point, pointIndex) =>
          pointIndex === index ? next : point,
        ),
      );
    };
    const onUp = () => {
      dragIndexRef.current = null;
    };
    window.addEventListener("pointermove", onMove);
    window.addEventListener("pointerup", onUp);
    return () => {
      window.removeEventListener("pointermove", onMove);
      window.removeEventListener("pointerup", onUp);
    };
  }, [onPointsChange, points]);

  const commitLabel = () => {
    if (Number(distanceMm) > 0) {
      setIsEditingLabel(false);
    }
  };

  const toPx = (point: NormalizedPoint) => ({
    x: point.x * size.width,
    y: point.y * size.height,
  });

  const lineEnd =
    points.length >= 2
      ? points[1]
      : points.length === 1 && previewPoint
        ? previewPoint
        : null;
  const lineStart = points[0];
  const hasLine = Boolean(lineStart && lineEnd);
  const startPx = lineStart ? toPx(lineStart) : null;
  const endPx = lineEnd ? toPx(lineEnd) : null;
  const dx = startPx && endPx ? endPx.x - startPx.x : 0;
  const dy = startPx && endPx ? endPx.y - startPx.y : 0;
  const length = Math.hypot(dx, dy);
  const angleDeg = (Math.atan2(dy, dx) * 180) / Math.PI;
  const shouldFlip = angleDeg > 90 || angleDeg < -90;
  const midPx =
    startPx && endPx
      ? { x: (startPx.x + endPx.x) / 2, y: (startPx.y + endPx.y) / 2 }
      : null;
  const displayedValue = distanceMm ? `${distanceMm} mm` : "mm";

  return (
    <div ref={containerRef} className="pointer-events-none absolute inset-0">
      {hasLine && startPx && (
        <div
          className={`absolute h-[2px] origin-left ${
            points.length === 2 ? "bg-blue-500" : "bg-blue-500/50"
          }`}
          style={{
            left: startPx.x,
            top: startPx.y,
            width: length,
            transform: `rotate(${angleDeg}deg)`,
          }}
        />
      )}
      {points.length === 2 && midPx && (
        <div
          className="pointer-events-none absolute"
          style={{
            left: midPx.x,
            top: midPx.y,
            // Keep the value box under the dimension chain (not centered on it),
            // matching BackdropImageModal / the pre-9ea21d0f calibration layout.
            width: length,
            height: 0,
            transform: `translate(-50%, -50%) rotate(${angleDeg}deg)`,
          }}
        >
          <div
            className="pointer-events-auto absolute left-1/2"
            style={{
              // Fixed px offset: parent is a zero-height midline, so % offsets
              // collapse and the box sat on the chain / endpoint handles.
              top: shouldFlip ? -28 : 16,
              transform: `translate(-50%, 0) rotate(${shouldFlip ? 180 : 0}deg)`,
            }}
            onClick={(event) => event.stopPropagation()}
            onMouseDown={(event) => event.stopPropagation()}
            onPointerDown={(event) => event.stopPropagation()}
          >
            {isEditingLabel ? (
              <label className="flex items-center gap-0.5 rounded border border-blue-400 bg-white px-1.5 py-0.5 shadow-sm">
                <input
                  ref={inputRef}
                  type="text"
                  inputMode="decimal"
                  value={distanceMm}
                  placeholder={DEFAULT_REFERENCE_DISTANCE_MM}
                  onChange={(event) => {
                    const raw = event.target.value.replace(/[^\d.]/g, "");
                    onDistanceChange(raw);
                  }}
                  onKeyDown={(event) => {
                    if (event.key === "Enter") {
                      event.preventDefault();
                      commitLabel();
                    }
                  }}
                  onFocus={(event) => event.currentTarget.select()}
                  onBlur={commitLabel}
                  onClick={(event) => {
                    event.stopPropagation();
                    event.currentTarget.select();
                  }}
                  className="w-12 select-text border-0 bg-transparent p-0 text-center text-xs font-medium text-black focus:outline-none focus:ring-0"
                  aria-label="Distance between points in millimetres"
                />
                <span className="text-[10px] font-medium text-black">mm</span>
              </label>
            ) : (
              <button
                type="button"
                onClick={(event) => {
                  event.stopPropagation();
                  setIsEditingLabel(true);
                }}
                className="rounded border border-blue-400 bg-white px-1.5 py-0.5 text-xs font-medium text-black shadow-sm hover:bg-white"
                aria-label="Edit reference distance"
              >
                {displayedValue}
              </button>
            )}
          </div>
        </div>
      )}
      {points.map((point, index) => {
        const px = toPx(point);
        return (
          <button
            key={`cal-point-${index}`}
            type="button"
            aria-label={index === 0 ? "Move start point" : "Move end point"}
            className="pointer-events-auto absolute h-3.5 w-3.5 -translate-x-1/2 -translate-y-1/2 cursor-move rounded-full border-2 border-white bg-blue-600 shadow hover:scale-125 focus:outline-none focus-visible:ring-2 focus-visible:ring-blue-500"
            style={{ left: px.x, top: px.y }}
            onClick={(event) => event.stopPropagation()}
            onPointerDown={(event) => {
              event.stopPropagation();
              event.preventDefault();
              dragIndexRef.current = index;
            }}
          />
        );
      })}
    </div>
  );
};

const TriStateCheckbox: React.FC<{
  state: "checked" | "mixed" | "unchecked";
  label: string;
  disabled?: boolean;
  onChange: () => void;
}> = ({ state, label, disabled, onChange }) => {
  const ref = React.useRef<HTMLInputElement>(null);
  React.useEffect(() => {
    if (ref.current) ref.current.indeterminate = state === "mixed";
  }, [state]);
  return (
    <input
      ref={ref}
      type="checkbox"
      checked={state === "checked"}
      disabled={disabled}
      onClick={(event) => event.stopPropagation()}
      onChange={onChange}
      aria-label={label}
      className="h-4 w-4 rounded border-gray-400 text-black focus:ring-black disabled:cursor-not-allowed disabled:opacity-45"
    />
  );
};

type SymbolCatalogEntry = ReturnType<
  typeof getFloorplanImportSymbolCatalog
>[number];

const SymbolOverrideControls: React.FC<{
  catalog: SymbolCatalogEntry[];
  libraryKey?: string | null;
  rotation: number;
  disabled: boolean;
  onChange: (override: { libraryKey: string; rotation: number }) => void;
}> = ({ catalog, libraryKey, rotation, disabled, onChange }) => {
  const categories = Array.from(
    new Set(catalog.map((entry) => entry.category)),
  );
  const degrees = Math.round(radiansToDegrees(rotation) * 10) / 10;
  const updateRotation = (nextDegrees: number) => {
    if (!libraryKey) return;
    onChange({ libraryKey, rotation: degreesToRadians(nextDegrees) });
  };
  return (
    <div className="mt-2 space-y-2 border-t border-[#e5e2da] pt-2">
      <label className="block">
        <span className="mb-1 block text-[11px] font-semibold text-[#555149]">
          BONI catalog symbol
        </span>
        <select
          value={libraryKey || ""}
          disabled={disabled}
          onChange={(event) =>
            onChange({ libraryKey: event.target.value, rotation })
          }
          className="w-full rounded-lg border border-[#bbb6a9] bg-white px-2.5 py-2 text-xs focus:border-black focus:outline-none focus:ring-1 focus:ring-black disabled:opacity-50"
        >
          <option value="">Choose a symbol…</option>
          {categories.map((category) => (
            <optgroup key={category} label={category}>
              {catalog
                .filter((entry) => entry.category === category)
                .map((entry) => (
                  <option key={entry.key} value={entry.key}>
                    {entry.label}
                  </option>
                ))}
            </optgroup>
          ))}
        </select>
      </label>
      <div className="flex items-center gap-2">
        <button
          type="button"
          disabled={disabled || !libraryKey}
          onClick={() => updateRotation(degrees - 90)}
          aria-label="Rotate symbol 90 degrees counter-clockwise"
          className="flex h-8 w-8 items-center justify-center rounded-lg border border-[#bbb6a9] bg-white hover:bg-[#f0eee7] focus:outline-none focus-visible:ring-2 focus-visible:ring-black disabled:opacity-45"
        >
          <FiRotateCcw size={14} />
        </button>
        <label className="flex min-w-0 flex-1 overflow-hidden rounded-lg border border-[#bbb6a9] bg-white focus-within:ring-2 focus-within:ring-black">
          <span className="sr-only">Symbol rotation in degrees</span>
          <input
            type="number"
            min="-180"
            max="180"
            step="1"
            value={degrees}
            disabled={disabled || !libraryKey}
            onChange={(event) =>
              updateRotation(normalizeDegrees(Number(event.target.value)))
            }
            className="min-w-0 flex-1 border-0 bg-transparent px-2.5 py-1.5 text-xs tabular-nums focus:ring-0"
          />
          <span className="flex items-center border-l border-[#ddd9cf] bg-[#f3f1eb] px-2 text-xs font-semibold">
            °
          </span>
        </label>
        <button
          type="button"
          disabled={disabled || !libraryKey}
          onClick={() => updateRotation(degrees + 90)}
          aria-label="Rotate symbol 90 degrees clockwise"
          className="flex h-8 w-8 items-center justify-center rounded-lg border border-[#bbb6a9] bg-white hover:bg-[#f0eee7] focus:outline-none focus-visible:ring-2 focus-visible:ring-black disabled:opacity-45"
        >
          <FiRotateCw size={14} />
        </button>
      </div>
    </div>
  );
};

const CandidateRow: React.FC<{
  entityRef: string;
  label: string;
  kind: CandidateKind;
  markerNumber: number;
  excluded: boolean;
  active: boolean;
  unavailable: boolean;
  disabled?: boolean;
  onToggle: () => void;
  onActivate: () => void;
  onHover: (hovered: boolean) => void;
}> = ({
  entityRef,
  label,
  kind,
  markerNumber,
  excluded,
  active,
  unavailable,
  disabled = false,
  onToggle,
  onActivate,
  onHover,
}) => (
  <div
    className={`flex items-center gap-2 rounded-lg border bg-white px-2 py-2 transition-[border-color,box-shadow] ${
      active
        ? "border-[#171713] shadow-[0_3px_10px_rgba(23,23,19,0.12)]"
        : "border-[#e5e2da] hover:border-[#bdb8aa]"
    }`}
    onMouseEnter={() => onHover(true)}
    onMouseLeave={() => onHover(false)}
  >
    <input
      type="checkbox"
      checked={!excluded}
      disabled={unavailable || disabled}
      onChange={onToggle}
      aria-label={`Include ${label} ${entityRef}`}
      className="h-4 w-4 rounded border-gray-400 text-black focus:ring-black disabled:cursor-not-allowed disabled:opacity-45"
    />
    <button
      type="button"
      onClick={onActivate}
      onFocus={() => onHover(true)}
      onBlur={() => onHover(false)}
      aria-pressed={active}
      className="flex min-w-0 flex-1 items-center gap-2 rounded-md text-left focus:outline-none focus-visible:ring-2 focus-visible:ring-black"
    >
      <span
        className="flex h-6 min-w-6 items-center justify-center rounded-full border-2 border-white px-1 text-[10px] font-bold leading-none text-white shadow-sm"
        style={{
          backgroundColor: excluded ? "#dc2626" : candidateColors[kind],
        }}
      >
        {markerNumber}
      </span>
      <span className="min-w-0 flex-1">
        <span className="block truncate text-sm font-medium text-[#171713]">
          {label}
        </span>
      </span>
    </button>
  </div>
);

const DraftOverlay: React.FC<{
  job: FloorplanImageImportFieldsFragment;
  excludedRefs: ReadonlySet<string>;
  candidateNumbers: ReadonlyMap<string, number>;
  highlightedRef?: string;
  zoom: number;
  /** Numbered marker circles; only used when review extras are enabled. */
  showMarkers?: boolean;
  /**
   * When true, draw highlighted chips on detected text labels so the user
   * can see what “Add text” will insert.
   */
  highlightLabels?: boolean;
}> = ({
  job,
  excludedRefs,
  candidateNumbers,
  highlightedRef,
  zoom,
  showMarkers = false,
  highlightLabels = false,
}) => {
  const draft = job.draft;
  if (!draft) return null;
  const widthM = (draft.sourceWidth * draft.scaleMmPerPixel) / 1000;
  const heightM = (draft.sourceHeight * draft.scaleMmPerPixel) / 1000;
  if (!(widthM > 0 && heightM > 0)) return null;
  const p = (point: { x: number; y: number }) => ({
    x: point.x,
    y: heightM - point.y,
  });
  const markerRadius = (Math.max(widthM, heightM) * 0.012) / zoom;
  const opacity = (ref: string) =>
    highlightedRef && highlightedRef !== ref ? 0.24 : 1;
  const color = (ref: string, kind: CandidateKind) =>
    excludedRefs.has(ref) ? "#dc2626" : candidateColors[kind];
  const marker = (
    ref: string,
    position: { x: number; y: number },
    kind: CandidateKind,
  ) => {
    if (!showMarkers) return null;
    const markerNumber = candidateNumbers.get(ref);
    if (!markerNumber) return null;
    const active = highlightedRef === ref;
    return (
      <g transform={`translate(${position.x} ${position.y})`}>
        <circle
          r={active ? markerRadius * 1.35 : markerRadius}
          fill={active ? "#f5cb2e" : color(ref, kind)}
          stroke={active ? "#171713" : "#ffffff"}
          strokeWidth={active ? 1.5 : 2}
          vectorEffect="non-scaling-stroke"
        />
        <text
          x="0"
          y="0"
          dy="0.35em"
          textAnchor="middle"
          fill={active ? "#171713" : "#ffffff"}
          fontSize={markerRadius * 1.05}
          fontWeight="800"
        >
          {markerNumber}
        </text>
      </g>
    );
  };
  const wallByRef = new Map(draft.walls.map((wall) => [wall.ref, wall]));

  return (
    <svg
      aria-hidden="true"
      viewBox={`0 0 ${widthM} ${heightM}`}
      preserveAspectRatio="none"
      className="pointer-events-none absolute inset-0 h-full w-full"
    >
      {draft.walls.map((wall) => {
        const start = p(wall.start);
        const end = p(wall.end);
        const middle = {
          x: (start.x + end.x) / 2,
          y: (start.y + end.y) / 2,
        };
        const active = highlightedRef === wall.ref;
        return (
          <g
            key={wall.ref}
            data-draft-ref={wall.ref}
            opacity={opacity(wall.ref)}
          >
            <line
              x1={start.x}
              y1={start.y}
              x2={end.x}
              y2={end.y}
              stroke="#ffffff"
              strokeWidth={active ? 7 : 5}
              vectorEffect="non-scaling-stroke"
            />
            <line
              x1={start.x}
              y1={start.y}
              x2={end.x}
              y2={end.y}
              stroke={color(wall.ref, "wall")}
              strokeWidth={active ? 4.5 : 3}
              strokeDasharray={excludedRefs.has(wall.ref) ? "7 5" : undefined}
              vectorEffect="non-scaling-stroke"
            />
            {marker(wall.ref, middle, "wall")}
          </g>
        );
      })}
      {draft.lines.map((line) => {
        const start = p(line.start);
        const end = p(line.end);
        const middle = {
          x: (start.x + end.x) / 2,
          y: (start.y + end.y) / 2,
        };
        return (
          <g
            key={line.ref}
            data-draft-ref={line.ref}
            opacity={opacity(line.ref)}
          >
            <line
              x1={start.x}
              y1={start.y}
              x2={end.x}
              y2={end.y}
              stroke="#ffffff"
              strokeWidth="4"
              vectorEffect="non-scaling-stroke"
            />
            <line
              x1={start.x}
              y1={start.y}
              x2={end.x}
              y2={end.y}
              stroke={color(line.ref, "line")}
              strokeWidth="2"
              strokeDasharray={excludedRefs.has(line.ref) ? "7 5" : undefined}
              vectorEffect="non-scaling-stroke"
            />
            {marker(line.ref, middle, "line")}
          </g>
        );
      })}
      {draft.openings.map((opening) => {
        const wall = wallByRef.get(opening.wallRef);
        if (!wall) return null;
        const dx = wall.end.x - wall.start.x;
        const dy = wall.end.y - wall.start.y;
        const length = Math.hypot(dx, dy);
        if (length <= 0) return null;
        const ux = dx / length;
        const uy = dy / length;
        const centerWorld = {
          x: wall.start.x + ux * opening.offset,
          y: wall.start.y + uy * opening.offset,
        };
        const start = p({
          x: centerWorld.x - (ux * opening.width) / 2,
          y: centerWorld.y - (uy * opening.width) / 2,
        });
        const end = p({
          x: centerWorld.x + (ux * opening.width) / 2,
          y: centerWorld.y + (uy * opening.width) / 2,
        });
        const center = p(centerWorld);
        return (
          <g
            key={opening.ref}
            data-draft-ref={opening.ref}
            opacity={opacity(opening.ref)}
          >
            <line
              x1={start.x}
              y1={start.y}
              x2={end.x}
              y2={end.y}
              stroke="#ffffff"
              strokeWidth="8"
              vectorEffect="non-scaling-stroke"
            />
            <line
              x1={start.x}
              y1={start.y}
              x2={end.x}
              y2={end.y}
              stroke={color(opening.ref, "opening")}
              strokeWidth="5"
              strokeDasharray={
                excludedRefs.has(opening.ref) ? "7 5" : undefined
              }
              vectorEffect="non-scaling-stroke"
            />
            {marker(opening.ref, center, "opening")}
          </g>
        );
      })}
      {draft.symbols.map((symbol) => {
        const position = p(symbol.position);
        const active = highlightedRef === symbol.ref;
        const detectedWidth = symbol.detectedWidth || symbol.width;
        const detectedHeight = symbol.detectedHeight || symbol.height;
        return (
          <g
            key={symbol.ref}
            data-draft-ref={symbol.ref}
            opacity={opacity(symbol.ref)}
          >
            <rect
              x={position.x - detectedWidth / 2}
              y={position.y - detectedHeight / 2}
              width={detectedWidth}
              height={detectedHeight}
              rx={markerRadius * 0.18}
              fill={color(symbol.ref, "symbol")}
              fillOpacity={active ? 0.3 : 0.15}
              stroke="#ffffff"
              strokeWidth={active ? 6 : 4}
              vectorEffect="non-scaling-stroke"
              transform={`rotate(${(-symbol.rotation * 180) / Math.PI} ${position.x} ${position.y})`}
            />
            <rect
              x={position.x - detectedWidth / 2}
              y={position.y - detectedHeight / 2}
              width={detectedWidth}
              height={detectedHeight}
              rx={markerRadius * 0.18}
              fill="none"
              stroke={color(symbol.ref, "symbol")}
              strokeWidth={active ? 3.5 : 2}
              strokeDasharray={excludedRefs.has(symbol.ref) ? "7 5" : undefined}
              vectorEffect="non-scaling-stroke"
              transform={`rotate(${(-symbol.rotation * 180) / Math.PI} ${position.x} ${position.y})`}
            />
            {marker(symbol.ref, position, "symbol")}
          </g>
        );
      })}
      {draft.labels.map((label) => {
        const position = p(label.position);
        const included = !excludedRefs.has(label.ref);
        const fontSize = Math.max(widthM, heightM) * 0.016;
        const padX = fontSize * 0.4;
        const padY = fontSize * 0.32;
        const textWidth = Math.max(label.text.trim().length, 1) * fontSize * 0.56;
        return (
          <g
            key={label.ref}
            data-draft-ref={label.ref}
            opacity={opacity(label.ref)}
          >
            {highlightLabels && included && label.text.trim() && (
              <g>
                <rect
                  x={position.x - textWidth / 2 - padX}
                  y={position.y - fontSize / 2 - padY}
                  width={textWidth + padX * 2}
                  height={fontSize + padY * 2}
                  rx={fontSize * 0.28}
                  fill="#fef08a"
                  stroke="#ca8a04"
                  strokeWidth={2}
                  vectorEffect="non-scaling-stroke"
                />
                <text
                  x={position.x}
                  y={position.y}
                  dy="0.35em"
                  textAnchor="middle"
                  fill="#713f12"
                  fontSize={fontSize}
                  fontWeight="800"
                >
                  {label.text}
                </text>
              </g>
            )}
            {marker(label.ref, position, "label")}
          </g>
        );
      })}
      {draft.measurements.map((measurement) => {
        const start = p(measurement.start);
        const end = p(measurement.end);
        const middle = {
          x: (start.x + end.x) / 2,
          y: (start.y + end.y) / 2,
        };
        return (
          <g
            key={measurement.ref}
            data-draft-ref={measurement.ref}
            opacity={opacity(measurement.ref)}
          >
            <line
              x1={start.x}
              y1={start.y}
              x2={end.x}
              y2={end.y}
              stroke={color(measurement.ref, "measurement")}
              strokeWidth="2"
              strokeDasharray={
                excludedRefs.has(measurement.ref) ? "7 5" : undefined
              }
              vectorEffect="non-scaling-stroke"
            />
            {marker(measurement.ref, middle, "measurement")}
          </g>
        );
      })}
    </svg>
  );
};

export const FloorplanImageImportModal: React.FC<Props> = ({
  isOpen,
  importId,
  onClose,
  onChanged,
}) => {
  const dialogRef = React.useRef<HTMLDivElement>(null);
  const fileInputRef = React.useRef<HTMLInputElement>(null);
  const reviewViewportRef = React.useRef<HTMLDivElement>(null);
  const initializedReviewJobRef = React.useRef<string | undefined>(undefined);
  const [jobId, setJobId] = React.useState(importId);
  const [mode, setMode] = React.useState<FloorplanImageImportMode>(
    FloorplanImageImportMode.FloorPlan,
  );
  const [file, setFile] = React.useState<File | null>(null);
  const [previewUrl, setPreviewUrl] = React.useState<string>();
  const [calibrationPoints, setCalibrationPoints] = React.useState<
    NormalizedPoint[]
  >([]);
  const [calibrationCursor, setCalibrationCursor] =
    React.useState<NormalizedPoint | null>(null);
  const [distanceMm, setDistanceMm] = React.useState("");
  const [scaleOverrideOpen, setScaleOverrideOpen] = React.useState(false);
  /** How scale is chosen before scanning: auto estimate or user reference. */
  const [scaleMode, setScaleMode] = React.useState<"auto" | "manual">("auto");
  const [excludedRefs, setExcludedRefs] = React.useState<Set<string>>(
    new Set(),
  );
  const [symbolOverrides, setSymbolOverrides] = React.useState<
    Map<string, { libraryKey: string; rotation: number }>
  >(new Map());
  const [overlayVisible, setOverlayVisible] = React.useState(true);
  const [imageZoom, setImageZoom] = React.useState(MIN_IMAGE_ZOOM);
  const [activeRef, setActiveRef] = React.useState<string>();
  const [hoveredRef, setHoveredRef] = React.useState<string>();
  const [error, setError] = React.useState<string>();
  const [warning, setWarning] = React.useState<string>();
  const [localApplied, setLocalApplied] = React.useState(false);
  const [applyingDraft, setApplyingDraft] = React.useState(false);

  const query = useFloorplanImageImportQuery({
    variables: { id: jobId || "" },
    skip: !jobId || !isOpen,
    pollInterval: jobId && isOpen ? 3000 : 0,
    fetchPolicy: "network-only",
  });
  const creditCostQuery = useFloorplanImageImportCreditCostQuery({
    skip: !isOpen,
    fetchPolicy: "cache-first",
  });
  const job = query.data?.floorplanImageImport;
  const [startImport, startState] = useStartFloorplanImageImportMutation();
  const [provideCalibration, calibrationState] =
    useProvideFloorplanImageImportCalibrationMutation();
  const [retryImport, retryState] = useRetryFloorplanImageImportMutation();
  const [cancelImport, cancelState] = useCancelFloorplanImageImportMutation();
  const [markApplied, markState] = useMarkFloorplanImageImportAppliedMutation();
  const [retryStage, retryStageState] =
    useRetryFloorplanImageImportStageMutation();
  const [updateReview, updateReviewState] =
    useUpdateFloorplanImageImportReviewMutation();
  const reviewSignatureRef = React.useRef<string | undefined>(undefined);
  const reviewPersistTimerRef = React.useRef<number | undefined>(undefined);
  const jobRef = React.useRef(job);
  const excludedRefsRef = React.useRef(excludedRefs);
  const symbolOverridesRef = React.useRef(symbolOverrides);
  jobRef.current = job;
  excludedRefsRef.current = excludedRefs;
  symbolOverridesRef.current = symbolOverrides;

  const flushPendingReview = React.useCallback(
    async ({ reportError = true }: { reportError?: boolean } = {}) => {
      if (reviewPersistTimerRef.current !== undefined) {
        window.clearTimeout(reviewPersistTimerRef.current);
        reviewPersistTimerRef.current = undefined;
      }
      const currentJob = jobRef.current;
      const currentExcluded = excludedRefsRef.current;
      const currentOverrides = symbolOverridesRef.current;
      if (
        !currentJob?.draft ||
        !isReviewableStatus(currentJob.status) ||
        currentJob.pipelineVersion !== "floorplan-image-v5" ||
        initializedReviewJobRef.current !==
          `${currentJob.id}:${currentJob.draftRevision}`
      ) {
        return;
      }
      const signature = reviewPersistenceSignature({
        draftRevision: currentJob.draftRevision,
        excludedRefs: currentExcluded,
        symbolOverrides: currentOverrides,
      });
      if (signature === reviewSignatureRef.current) return;
      try {
        await updateReview({
          variables: {
            id: currentJob.id,
            draftRevision: currentJob.draftRevision,
            review: {
              excludedEntityIds: [...currentExcluded],
              symbolOverrides: [...currentOverrides.entries()].map(
                ([ref, override]) => ({ ref, ...override }),
              ),
            },
          },
        });
        reviewSignatureRef.current = signature;
      } catch (reviewError) {
        if (reportError) {
          setError(getErrorMessage(reviewError));
          await query.refetch();
        }
        throw reviewError;
      }
    },
    [query.refetch, updateReview],
  );

  const closeModal = React.useCallback(() => {
    void flushPendingReview({ reportError: false }).catch(() => undefined);
    onClose();
  }, [flushPendingReview, onClose]);

  React.useEffect(() => {
    if (!isOpen) return;
    setJobId(importId);
    setExcludedRefs(new Set());
    setSymbolOverrides(new Map());
    setOverlayVisible(true);
    setImageZoom(MIN_IMAGE_ZOOM);
    setActiveRef(undefined);
    setHoveredRef(undefined);
    initializedReviewJobRef.current = undefined;
    setError(undefined);
    setWarning(undefined);
    setLocalApplied(false);
    setApplyingDraft(false);
    setScaleOverrideOpen(false);
    setFile(null);
    if (!importId) {
      setCalibrationPoints([]);
      setCalibrationCursor(null);
      setDistanceMm("");
      setScaleMode("auto");
      setMode(FloorplanImageImportMode.FloorPlan);
    }
  }, [importId, isOpen]);

  React.useEffect(() => {
    if (!isOpen || !jobId) {
      query.stopPolling();
      return;
    }
    if (job && !activeStatuses.has(job.status)) {
      query.stopPolling();
      return;
    }
    if (job && activeStatuses.has(job.status)) {
      query.startPolling(3000);
    }
  }, [isOpen, jobId, job?.status]);

  const reviewLocked =
    applyingDraft || Boolean(job && activeStatuses.has(job.status));

  React.useEffect(() => {
    if (!isOpen) return;
    if (reviewLocked && scaleOverrideOpen) {
      setScaleOverrideOpen(false);
      setCalibrationPoints([]);
      setCalibrationCursor(null);
      setDistanceMm("");
    }
  }, [isOpen, reviewLocked, scaleOverrideOpen]);

  // Freeze the image at fit while the server is tracing — zoom is disabled then,
  // and a leftover zoom from calibration would only confuse the progress view.
  React.useEffect(() => {
    if (reviewLocked) {
      setImageZoom(MIN_IMAGE_ZOOM);
    }
  }, [reviewLocked]);

  React.useEffect(() => {
    if (
      !job?.draft ||
      !isReviewableStatus(job.status) ||
      initializedReviewJobRef.current === `${job.id}:${job.draftRevision}`
    ) {
      return;
    }
    const { excludedRefs: nextExcluded, symbolOverrides: nextOverrides } =
      resolveFloorplanImportReviewSelection(job);
    setExcludedRefs(nextExcluded);
    setSymbolOverrides(nextOverrides);
    initializedReviewJobRef.current = `${job.id}:${job.draftRevision}`;
    reviewSignatureRef.current = reviewPersistenceSignature({
      draftRevision: job.draftRevision,
      excludedRefs: nextExcluded,
      symbolOverrides: nextOverrides,
    });
  }, [job?.draft, job?.draftRevision, job?.id, job?.reviewState, job?.status]);

  React.useEffect(() => {
    if (
      !job?.draft ||
      !isReviewableStatus(job.status) ||
      job.pipelineVersion !== "floorplan-image-v5" ||
      initializedReviewJobRef.current !== `${job.id}:${job.draftRevision}`
    ) {
      return;
    }
    const signature = reviewPersistenceSignature({
      draftRevision: job.draftRevision,
      excludedRefs,
      symbolOverrides,
    });
    if (signature === reviewSignatureRef.current) return;
    if (reviewPersistTimerRef.current !== undefined) {
      window.clearTimeout(reviewPersistTimerRef.current);
    }
    reviewPersistTimerRef.current = window.setTimeout(() => {
      void updateReview({
        variables: {
          id: job.id,
          draftRevision: job.draftRevision,
          review: {
            excludedEntityIds: [...excludedRefs],
            symbolOverrides: [...symbolOverrides.entries()].map(
              ([ref, override]) => ({ ref, ...override }),
            ),
          },
        },
      })
        .then(() => {
          reviewSignatureRef.current = signature;
        })
        .catch(async (reviewError) => {
          setError(getErrorMessage(reviewError));
          await query.refetch();
        });
    }, 500);
    return () => {
      if (reviewPersistTimerRef.current !== undefined) {
        window.clearTimeout(reviewPersistTimerRef.current);
      }
    };
  }, [
    excludedRefs,
    job?.draft,
    job?.draftRevision,
    job?.id,
    job?.pipelineVersion,
    job?.status,
    query.refetch,
    symbolOverrides,
    updateReview,
  ]);

  React.useEffect(() => {
    if (!activeRef || !overlayVisible || !reviewViewportRef.current) return;
    const viewport = reviewViewportRef.current;
    const entity = Array.from(
      viewport.querySelectorAll<SVGGElement>("[data-draft-ref]"),
    ).find((element) => element.dataset.draftRef === activeRef);
    if (!entity) return;
    const viewportRect = viewport.getBoundingClientRect();
    const entityRect = entity.getBoundingClientRect();
    viewport.scrollTo({
      left:
        viewport.scrollLeft +
        entityRect.left +
        entityRect.width / 2 -
        (viewportRect.left + viewportRect.width / 2),
      top:
        viewport.scrollTop +
        entityRect.top +
        entityRect.height / 2 -
        (viewportRect.top + viewportRect.height / 2),
      behavior: "smooth",
    });
  }, [activeRef, overlayVisible]);

  React.useEffect(() => {
    if (!file) {
      setPreviewUrl(undefined);
      return;
    }
    const url = URL.createObjectURL(file);
    setPreviewUrl(url);
    return () => URL.revokeObjectURL(url);
  }, [file]);

  React.useEffect(() => {
    if (!isOpen) return;
    const previousActive = document.activeElement as HTMLElement | null;
    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    requestAnimationFrame(() => dialogRef.current?.focus());
    const keydown = (event: KeyboardEvent) => {
      if (event.key === "Escape") {
        event.preventDefault();
        closeModal();
        return;
      }
      if (event.key !== "Tab" || !dialogRef.current) return;
      const focusable = Array.from(
        dialogRef.current.querySelectorAll<HTMLElement>(
          'button:not([disabled]), input:not([disabled]), [tabindex]:not([tabindex="-1"])',
        ),
      );
      if (!focusable.length) return;
      const first = focusable[0];
      const last = focusable[focusable.length - 1];
      if (event.shiftKey && document.activeElement === first) {
        event.preventDefault();
        last.focus();
      } else if (!event.shiftKey && document.activeElement === last) {
        event.preventDefault();
        first.focus();
      }
    };
    document.addEventListener("keydown", keydown, true);
    return () => {
      document.body.style.overflow = previousOverflow;
      document.removeEventListener("keydown", keydown, true);
      previousActive?.focus();
    };
  }, [closeModal, isOpen]);

  const chooseImage = (nextFile?: File) => {
    setError(undefined);
    if (!nextFile) return;
    if (!ACCEPTED_IMAGE_TYPES.includes(nextFile.type)) {
      setError("Choose a JPG, PNG, or WebP image.");
      return;
    }
    if (nextFile.size > MAX_IMAGE_BYTES) {
      setError("Choose an image smaller than 20 MB.");
      return;
    }
    setFile(nextFile);
    setCalibrationPoints([]);
    setCalibrationCursor(null);
    setDistanceMm("");
    setImageZoom(MIN_IMAGE_ZOOM);
  };

  const addCalibrationPoint = (event: React.MouseEvent<HTMLImageElement>) => {
    const calibratingBeforeScan =
      !jobId && scaleMode === "manual" && Boolean(file);
    if (!scaleOverrideOpen && !calibratingBeforeScan) return;
    // Alt+click is reserved for panning while zoomed during calibration.
    if (event.altKey) return;
    const rect = event.currentTarget.getBoundingClientRect();
    let next = {
      x: Math.min(1, Math.max(0, (event.clientX - rect.left) / rect.width)),
      y: Math.min(1, Math.max(0, (event.clientY - rect.top) / rect.height)),
    };
    const current = calibrationPoints;
    if (current.length >= 2) {
      setCalibrationPoints([next]);
      setDistanceMm("");
      setCalibrationCursor(null);
      return;
    }
    if (
      current.length === 1 &&
      !event.shiftKey &&
      rect.width > 0 &&
      rect.height > 0
    ) {
      next = snapCalibrationPointTo45(
        current[0],
        next,
        rect.width,
        rect.height,
      );
    }
    const updated = [...current, next];
    setCalibrationPoints(updated);
    // After the second click, show blue markers with a default length ready to edit.
    if (updated.length === 2) {
      setDistanceMm(DEFAULT_REFERENCE_DISTANCE_MM);
    }
    setCalibrationCursor(null);
  };

  const updateCalibrationCursor = (
    event: React.MouseEvent<HTMLImageElement>,
  ) => {
    const calibratingBeforeScan =
      !jobId && scaleMode === "manual" && Boolean(file);
    if (
      (!scaleOverrideOpen && !calibratingBeforeScan) ||
      calibrationPoints.length !== 1
    ) {
      return;
    }
    const rect = event.currentTarget.getBoundingClientRect();
    let next = {
      x: Math.min(1, Math.max(0, (event.clientX - rect.left) / rect.width)),
      y: Math.min(1, Math.max(0, (event.clientY - rect.top) / rect.height)),
    };
    if (!event.shiftKey && rect.width > 0 && rect.height > 0) {
      next = snapCalibrationPointTo45(
        calibrationPoints[0],
        next,
        rect.width,
        rect.height,
      );
    }
    setCalibrationCursor(next);
  };

  const submit = async () => {
    if (!file) {
      setError("Choose an image to continue.");
      return;
    }
    if (
      scaleMode === "manual" &&
      (calibrationPoints.length !== 2 || !(Number(distanceMm) > 0))
    ) {
      setError(
        "Mark both ends of a known distance and enter its real length, or switch to automatic scale.",
      );
      return;
    }
    setError(undefined);
    try {
      const catalog = getFloorplanImportSymbolCatalog(mode);
      const result = await startImport({
        variables: {
          image: file,
          input: {
            floorplanId: floorplannerStore.id || undefined,
            name: file.name.replace(/\.(jpe?g|png|webp)$/i, ""),
            mode,
            symbolCatalog: catalog.map((entry) => ({ ...entry, tab: mode })),
            ...(scaleMode === "manual"
              ? {
                  calibration: {
                    start: calibrationPoints[0],
                    end: calibrationPoints[1],
                    distanceMm: Number(distanceMm),
                  },
                }
              : {}),
          },
        },
      });
      const created = result.data?.startFloorplanImageImport;
      if (!created?.id) throw new Error("The import could not be started.");
      setJobId(created.id);
      setScaleOverrideOpen(false);
      onChanged?.();
    } catch (submitError) {
      setError(getErrorMessage(submitError));
    }
  };

  const beginScaleOverride = () => {
    if (job && activeStatuses.has(job.status)) return;
    setCalibrationPoints([]);
    setCalibrationCursor(null);
    setDistanceMm("");
    setScaleOverrideOpen(true);
    setError(undefined);
  };

  const cancelScaleOverride = () => {
    setCalibrationPoints([]);
    setCalibrationCursor(null);
    setDistanceMm("");
    setScaleOverrideOpen(false);
    setError(undefined);
  };

  const saveScaleOverride = async () => {
    if (job && activeStatuses.has(job.status)) return;
    if (!job || calibrationPoints.length !== 2 || !(Number(distanceMm) > 0)) {
      setError("Mark two reference points and enter their real distance.");
      return;
    }
    setError(undefined);
    try {
      await provideCalibration({
        variables: {
          id: job.id,
          calibration: {
            start: calibrationPoints[0],
            end: calibrationPoints[1],
            distanceMm: Number(distanceMm),
          },
        },
      });
      await query.refetch();
      setCalibrationPoints([]);
      setCalibrationCursor(null);
      setDistanceMm("");
      setScaleOverrideOpen(false);
      onChanged?.();
    } catch (calibrationError) {
      setError(getErrorMessage(calibrationError));
    }
  };

  const retry = async () => {
    if (!job) return;
    setError(undefined);
    try {
      await retryImport({ variables: { id: job.id } });
      query.startPolling(3000);
      await query.refetch();
      onChanged?.();
    } catch (retryError) {
      setError(getErrorMessage(retryError));
    }
  };

  const cancel = async () => {
    if (!job) return;
    setError(undefined);
    try {
      await cancelImport({ variables: { id: job.id } });
      onChanged?.();
      onClose();
    } catch (cancelError) {
      setError(getErrorMessage(cancelError));
    }
  };

  const isImportInsertedInPlan = (importId?: string) =>
    floorplannerStore.hasFloorplanImportInPlan(importId);

  const apply = async () => {
    if (!job?.draft) return;
    if (floorplannerStore.lockedByOther || floorplannerStore.collabAccessLost) {
      setError(
        "This floorplan is view-only while another collaborator is editing.",
      );
      return;
    }
    setError(undefined);
    setWarning(undefined);
    setApplyingDraft(true);
    let insertedThisAttempt = false;
    let drawingPresent = localApplied;
    let backdropWarning: string | undefined;
    try {
      await flushPendingReview();
      // Always insert on this button (including "Insert another copy").
      // applyingDraft disables the control so double-clicks cannot stack.
      // localApplied only tracks whether geometry landed for markApplied errors.
      await new Promise<void>((resolve) =>
        window.requestAnimationFrame(() => resolve()),
      );
      const result = await applyFloorplanImportDraft({
        draft: job.draft,
        excludedRefs,
        symbolOverrides,
        importId: job.id,
        sourceImageUrl:
          resolveUserModel3DUrl(job.previewUrl) || previewUrl || undefined,
      });
      insertedThisAttempt = !result.alreadyApplied;
      drawingPresent = true;
      backdropWarning = result.backdropWarning;
      setLocalApplied(true);
      if (job.status !== FloorplanImageImportStatus.Applied) {
        const persistApplied = () =>
          markApplied({
            variables: {
              id: job.id,
              excludedEntityIds: Array.from(excludedRefs),
            },
          });
        try {
          await persistApplied();
        } catch {
          await persistApplied();
        }
      }
      onChanged?.();
      if (backdropWarning) {
        setWarning(backdropWarning);
        return;
      }
      onClose();
    } catch (applyError) {
      setError(
        drawingPresent || insertedThisAttempt
          ? MARK_APPLIED_AFTER_INSERT_ERROR
          : getErrorMessage(applyError),
      );
    } finally {
      setApplyingDraft(false);
    }
  };

  const toggleExcluded = (ref: string) => {
    setExcludedRefs((current) => {
      const wallOpeningRefs = new Map<string, string[]>();
      job?.draft?.openings.forEach((opening) => {
        const currentRefs = wallOpeningRefs.get(opening.wallRef) ?? [];
        currentRefs.push(opening.ref);
        wallOpeningRefs.set(opening.wallRef, currentRefs);
      });
      return toggleReviewRef({
        excludedRefs: current,
        ref,
        wallOpeningRefs,
      });
    });
  };

  const setSymbolOverride = (
    ref: string,
    override: { libraryKey: string; rotation: number },
  ) => {
    setSymbolOverrides((current) => {
      const next = new Map(current);
      next.set(ref, override);
      return next;
    });
    if (override.libraryKey) {
      setExcludedRefs((current) => {
        const next = new Set(current);
        next.delete(ref);
        return next;
      });
    }
  };

  const retryLayer = async (stage: FloorplanImageImportStage) => {
    if (!job) return;
    setError(undefined);
    try {
      await retryStage({ variables: { id: job.id, stage } });
      query.startPolling(3000);
      await query.refetch();
      onChanged?.();
    } catch (retryError) {
      setError(getErrorMessage(retryError));
    }
  };

  if (!isOpen) return null;

  const sourceUrl =
    previewUrl || resolveUserModel3DUrl(job?.previewUrl) || undefined;
  // Always show the source image during analysis — partial wall overlays are
  // reserved for the finished reviewable draft.
  const displaySourceUrl = sourceUrl;
  const symbolCatalog = getFloorplanImportSymbolCatalog(job?.mode ?? mode);
  const catalogByKey = new Map(
    symbolCatalog.map((entry) => [entry.key, entry]),
  );
  const candidates: DraftCandidate[] = job?.draft
    ? [
        ...job.draft.walls.map((item) => ({
          ref: item.ref,
          label: "Wall",
          kind: "wall" as const,
          insertable: true,
          root: "structure" as const,
          subgroup: "Walls",
        })),
        ...job.draft.lines.map((item) => ({
          ref: item.ref,
          label: "Elevation line",
          kind: "line" as const,
          insertable: true,
          root: "structure" as const,
          subgroup: "Lines",
        })),
        ...job.draft.openings.map((item) => ({
          ref: item.ref,
          label:
            item.kind === "DOUBLE_DOOR"
              ? "Double door"
              : item.kind === "WINDOW"
                ? "Window"
                : item.kind === "VOID"
                  ? "Passage"
                  : "Door",
          kind: "opening" as const,
          insertable: true,
          root: "structure" as const,
          subgroup:
            item.kind === "WINDOW"
              ? "Windows"
              : item.kind === "VOID"
                ? "Passages"
                : "Doors",
        })),
        ...job.draft.symbols.map((item) => ({
          ref: item.ref,
          label:
            symbolOverrides.get(item.ref)?.libraryKey || item.libraryKey
              ? item.semanticType
              : getImportedRectangleFallback(item.semanticType)
                ? `${item.semanticType} · rectangle`
                : `${item.semanticType} · no BONI match`,
          kind: "symbol" as const,
          insertable:
            Boolean(symbolOverrides.get(item.ref)?.libraryKey) ||
            isFloorplanImportSymbolInsertable(item),
          root: "symbols" as const,
          subgroup:
            catalogByKey.get(
              symbolOverrides.get(item.ref)?.libraryKey ||
                item.libraryKey ||
                "",
            )?.category ||
            getImportedRectangleFallback(item.semanticType)?.label ||
            "Unmatched",
        })),
        ...job.draft.labels.map((item) => ({
          ref: item.ref,
          label: `Label: ${item.text}`,
          kind: "label" as const,
          insertable: true,
          root: "annotations" as const,
          subgroup: "Labels",
        })),
        ...job.draft.measurements.map((item) => ({
          ref: item.ref,
          label: `Measure: ${Math.round(item.valueMm)} mm`,
          kind: "measurement" as const,
          insertable: true,
          root: "annotations" as const,
          subgroup: "Measurements",
        })),
      ]
    : [];
  const candidateNumbers = new Map(
    candidates.map((candidate, index) => [candidate.ref, index + 1]),
  );
  const wallOpeningRefs = new Map<string, string[]>();
  job?.draft?.openings.forEach((opening) => {
    const refs = wallOpeningRefs.get(opening.wallRef) ?? [];
    refs.push(opening.ref);
    wallOpeningRefs.set(opening.wallRef, refs);
  });
  const rootRefs = (root: ReviewRoot) =>
    candidates
      .filter((candidate) => candidate.root === root)
      .map((candidate) => candidate.ref);
  const selectedInsertableCount = candidates.filter(
    (candidate) => candidate.insertable && !excludedRefs.has(candidate.ref),
  ).length;
  const labelRefs = (job?.draft?.labels ?? []).map((label) => label.ref);
  const structureCandidateCount = candidates.filter(
    (candidate) => candidate.kind !== "label",
  ).length;
  /** On by default when labels exist; user can opt out before insert. */
  const includeTextAnnotations =
    labelRefs.length > 0 &&
    labelRefs.every((ref) => !excludedRefs.has(ref));
  const alreadyInsertedInPlan = isImportInsertedInPlan(job?.id);
  const setCandidatesIncluded = (refs: string[], included: boolean) => {
    setExcludedRefs((current) =>
      setReviewRefsIncluded({
        excludedRefs: current,
        refs,
        included,
        wallOpeningRefs,
      }),
    );
  };
  const setIncludeTextAnnotations = (included: boolean) => {
    if (!labelRefs.length) return;
    setCandidatesIncluded(labelRefs, included);
  };
  const stageState = (stage: FloorplanImageImportStage) =>
    job?.stageStates.find((state) => state.stage === stage);
  const unavailableLayers = [
    {
      label: "Doors and windows",
      stage: FloorplanImageImportStage.DetectingOpenings,
      state: stageState(FloorplanImageImportStage.DetectingOpenings),
    },
    // Symbols layer intentionally omitted while scanning is disabled.
    {
      label: "Labels and measurements",
      stage: FloorplanImageImportStage.ReadingAnnotations,
      state: stageState(FloorplanImageImportStage.ReadingAnnotations),
    },
  ].filter(
    (layer) =>
      layer.state &&
      [
        FloorplanImportStageStatus.Failed,
        FloorplanImportStageStatus.Skipped,
      ].includes(layer.state.status),
  );
  const highlightedRef = hoveredRef || activeRef;
  const scaleSource = job?.draft?.scaleSource;
  return createPortal(
    <div
      className="fixed inset-0 z-[10000] flex items-center justify-center bg-black/50 p-3 sm:p-6"
      onMouseDown={(event) => event.stopPropagation()}
      onClick={(event) => {
        event.stopPropagation();
        if (event.target === event.currentTarget) closeModal();
      }}
    >
      <div
        ref={dialogRef}
        role="dialog"
        aria-modal="true"
        aria-labelledby="floorplan-import-title"
        tabIndex={-1}
        className="relative flex max-h-[calc(100vh-24px)] w-full max-w-6xl flex-col overflow-hidden rounded-2xl bg-[#f8f7f3] shadow-2xl focus:outline-none"
      >
        <header className="flex items-start justify-between border-b border-[#dedbd2] px-5 py-4 sm:px-7">
          <div>
            <div className="flex items-center gap-2">
              <HiSparkles className="h-5 w-5 text-[#ad8500]" />
              <h2
                id="floorplan-import-title"
                className="text-xl font-semibold text-[#171713]"
              >
                Create editable drawing from image
              </h2>
            </div>
            <p className="mt-1 text-sm text-[#69655d]">
              BONI detects structures, then lets you verify the draft before
              inserting it.
            </p>
          </div>
          <button
            type="button"
            onClick={closeModal}
            aria-label="Close image import"
            className="flex h-10 w-10 items-center justify-center rounded-full bg-white text-[#171713] shadow-sm hover:bg-[#eeece5] focus:outline-none focus-visible:ring-2 focus-visible:ring-black"
          >
            <IoClose size={20} />
          </button>
        </header>

        <div className="flex min-h-0 flex-1 flex-col overflow-y-auto lg:grid lg:grid-cols-[minmax(0,1.55fr)_minmax(320px,0.75fr)] lg:overflow-hidden">
          <section className="shrink-0 border-b border-[#dedbd2] p-5 sm:p-7 lg:min-h-0 lg:overflow-y-auto lg:border-b-0 lg:border-r">
            {!jobId ? (
              <div className="space-y-5">
                <div>
                  <div className="mb-2 text-xs font-semibold uppercase tracking-[0.12em] text-[#77736a]">
                    Drawing type
                  </div>
                  <div className="grid gap-2 sm:grid-cols-2">
                    {[
                      [
                        FloorplanImageImportMode.FloorPlan,
                        "Floorplan",
                        "Walls, openings and furniture",
                      ],
                      [
                        FloorplanImageImportMode.Elevation,
                        "Elevation",
                        "Facade lines and elevation symbols",
                      ],
                    ].map(([value, title, description]) => (
                      <button
                        key={value}
                        type="button"
                        onClick={() =>
                          setMode(value as FloorplanImageImportMode)
                        }
                        className={`rounded-xl border p-3 text-left ${mode === value ? "border-[#171713] bg-white ring-1 ring-[#171713]" : "border-[#dedbd2] bg-[#f3f1eb] hover:bg-white"}`}
                      >
                        <span className="block text-sm font-semibold">
                          {title}
                        </span>
                        <span className="mt-1 block text-xs leading-4 text-[#77736a]">
                          {description}
                        </span>
                      </button>
                    ))}
                  </div>
                </div>

                {!sourceUrl ? (
                  <button
                    type="button"
                    onClick={() => fileInputRef.current?.click()}
                    onDrop={(event) => {
                      event.preventDefault();
                      chooseImage(event.dataTransfer.files?.[0]);
                    }}
                    onDragOver={(event) => event.preventDefault()}
                    className="flex min-h-72 w-full flex-col items-center justify-center rounded-2xl border border-dashed border-[#aaa598] bg-white px-6 text-center hover:border-[#171713] focus:outline-none focus-visible:ring-2 focus-visible:ring-black"
                  >
                    <span className="flex h-14 w-14 items-center justify-center rounded-full bg-[#f0eee7]">
                      <FiUploadCloud size={24} />
                    </span>
                    <span className="mt-4 text-sm font-semibold">
                      Drop a drawing here or choose an image
                    </span>
                    <span className="mt-1 text-xs text-[#77736a]">
                      JPG, PNG or WebP · up to 20 MB
                    </span>
                  </button>
                ) : (
                  <div>
                    <div className="mb-2 flex flex-wrap items-center justify-between gap-3">
                      <div>
                        <span className="block text-sm font-medium">
                          {scaleMode === "manual"
                            ? "Set a reference measurement"
                            : "Review the source image"}
                        </span>
                        <span className="mt-0.5 block text-xs text-[#69655d]">
                          {scaleMode === "manual"
                            ? "Set a reference measurement. Mark a known distance in the image by setting two reference points, then enter the actual length. Drag the points to adjust. Hold Shift to move the line freely without snapping to 45-degree angles. Alt-drag or middle-mouse to pan."
                            : "BONI will estimate scale while scanning. You can still adjust it before insertion."}
                        </span>
                      </div>
                      <div className="flex items-center gap-3">
                        {TRACE_MODAL_ZOOM_ENABLED && scaleMode === "manual" && (
                          <ImageZoomControls
                            zoom={imageZoom}
                            onChange={setImageZoom}
                          />
                        )}
                        <button
                          type="button"
                          onClick={() => fileInputRef.current?.click()}
                          className="text-xs font-medium underline underline-offset-2"
                        >
                          Replace image
                        </button>
                      </div>
                    </div>
                    <ZoomableImageCanvas
                      sourceUrl={sourceUrl!}
                      alt="Drawing to reconstruct"
                      zoom={imageZoom}
                      onZoomChange={setImageZoom}
                      zoomEnabled={
                        TRACE_MODAL_ZOOM_ENABLED && scaleMode === "manual"
                      }
                      onImageClick={
                        scaleMode === "manual" ? addCalibrationPoint : undefined
                      }
                      onImageMouseMove={
                        scaleMode === "manual"
                          ? updateCalibrationCursor
                          : undefined
                      }
                      onImageMouseLeave={() => setCalibrationCursor(null)}
                      viewportClassName="h-[56vh] min-h-[320px] max-h-[720px] overflow-auto rounded-xl border border-[#d3d0c7] bg-white"
                    >
                      {scaleMode === "manual" && (
                        <CalibrationOverlay
                          points={calibrationPoints}
                          previewPoint={calibrationCursor}
                          distanceMm={distanceMm}
                          onDistanceChange={setDistanceMm}
                          onPointsChange={setCalibrationPoints}
                        />
                      )}
                    </ZoomableImageCanvas>
                    {scaleMode === "manual" && (
                      <p className="mt-2 text-xs text-[#69655d]">
                        {calibrationPoints.length === 0
                          ? "Click the first end of the reference"
                          : calibrationPoints.length === 1
                            ? "Click the second end"
                            : Number(distanceMm) > 0
                              ? "Reference set · Ready to scan"
                              : "Enter the real length next to the line"}
                      </p>
                    )}
                  </div>
                )}
                <input
                  ref={fileInputRef}
                  type="file"
                  accept="image/jpeg,image/png,image/webp"
                  className="hidden"
                  onChange={(event) => chooseImage(event.target.files?.[0])}
                />
              </div>
            ) : displaySourceUrl ? (
              <div className="relative h-full min-h-[420px] overflow-hidden rounded-xl border border-[#d3d0c7] bg-white">
                {TRACE_MODAL_ZOOM_ENABLED &&
                  !reviewLocked &&
                  (FLOORPLAN_IMPORT_REVIEW_EXTRA_CONTROLS_ENABLED ||
                    !job ||
                    !isReviewableStatus(job.status) ||
                    scaleOverrideOpen) && (
                  <div className="absolute right-3 top-3 z-20 flex items-center gap-2">
                    <span className="hidden rounded-md border border-[#dedbd2] bg-white/95 px-2 py-1.5 text-[11px] font-medium text-[#555149] shadow-sm lg:block">
                      {scaleOverrideOpen
                        ? "Wheel to zoom · Alt-drag or middle-mouse to pan"
                        : "Wheel to zoom · drag to pan"}
                    </span>
                    <ImageZoomControls
                      zoom={imageZoom}
                      onChange={setImageZoom}
                    />
                  </div>
                )}
                <ZoomableImageCanvas
                  sourceUrl={displaySourceUrl}
                  alt="Imported drawing source"
                  zoom={imageZoom}
                  onZoomChange={setImageZoom}
                  zoomEnabled={
                    TRACE_MODAL_ZOOM_ENABLED &&
                    !reviewLocked &&
                    (FLOORPLAN_IMPORT_REVIEW_EXTRA_CONTROLS_ENABLED ||
                      !job ||
                      !isReviewableStatus(job.status) ||
                      scaleOverrideOpen)
                  }
                  onImageClick={
                    scaleOverrideOpen ? addCalibrationPoint : undefined
                  }
                  onImageMouseMove={
                    scaleOverrideOpen ? updateCalibrationCursor : undefined
                  }
                  onImageMouseLeave={() => setCalibrationCursor(null)}
                  viewportRef={reviewViewportRef}
                  viewportClassName="h-full w-full overflow-auto"
                >
                  {job?.draft && isReviewableStatus(job.status) && overlayVisible && (
                      <DraftOverlay
                        job={job}
                        excludedRefs={excludedRefs}
                        candidateNumbers={candidateNumbers}
                        highlightedRef={highlightedRef}
                        zoom={imageZoom}
                        showMarkers={FLOORPLAN_IMPORT_REVIEW_EXTRA_CONTROLS_ENABLED}
                        highlightLabels={includeTextAnnotations}
                      />
                    )}
                  {scaleOverrideOpen && (
                    <CalibrationOverlay
                      points={calibrationPoints}
                      previewPoint={calibrationCursor}
                      distanceMm={distanceMm}
                      onDistanceChange={setDistanceMm}
                      onPointsChange={setCalibrationPoints}
                    />
                  )}
                </ZoomableImageCanvas>
                {job && activeStatuses.has(job.status) && (
                  <div className="absolute inset-x-3 bottom-3 z-30 rounded-xl bg-[#171713]/95 px-4 py-3 text-white shadow-[0_8px_30px_rgba(23,23,19,0.28)]">
                    <div className="flex items-center gap-3" aria-live="polite">
                      <FiRefreshCw className="h-5 w-5 shrink-0 animate-spin text-[#f5cb2e] motion-reduce:animate-none" />
                      <div className="min-w-0 flex-1">
                        <div className="flex items-center justify-between gap-3 text-sm font-semibold">
                          <span className="truncate">
                            {stageLabel(job.stage)}
                          </span>
                          <span className="tabular-nums">
                            {Math.round(job.progress * 100)}%
                          </span>
                        </div>
                        <div className="mt-2 h-1.5 overflow-hidden rounded-full bg-white/20">
                          <div
                            className="h-full rounded-full bg-[#f5cb2e] transition-[width] motion-reduce:transition-none"
                            style={{
                              width: `${Math.round(job.progress * 100)}%`,
                            }}
                          />
                        </div>
                        <div className="mt-1.5 text-[11px] text-[#e5e1d8]">
                          {stageState(job.stage)?.entityCount
                            ? `${stageState(job.stage)?.entityCount} detected in this layer · `
                            : ""}
                          You can close this window and return later.
                        </div>
                      </div>
                    </div>
                  </div>
                )}
              </div>
            ) : (
              <div className="flex h-full min-h-[420px] items-center justify-center rounded-xl border border-[#d3d0c7] bg-white text-sm text-[#77736a]">
                <FiImage className="mr-2" /> Loading source image…
              </div>
            )}
          </section>

          <aside className="shrink-0 p-5 sm:p-7 lg:min-h-0 lg:overflow-y-auto">
            {!jobId ? (
              <div className="space-y-5">
                <div>
                  <h3 className="text-base font-semibold">Scale</h3>
                  <p className="mt-1 text-sm leading-5 text-[#69655d]">
                    Choose how the drawing size is set before scanning. You can
                    still fine-tune later if needed.
                  </p>
                </div>
                <div className="grid gap-2">
                  <button
                    type="button"
                    onClick={() => {
                      setScaleMode("auto");
                      setCalibrationPoints([]);
                      setCalibrationCursor(null);
                      setDistanceMm("");
                      setImageZoom(MIN_IMAGE_ZOOM);
                    }}
                    className={`rounded-xl border p-3 text-left transition-colors ${
                      scaleMode === "auto"
                        ? "border-[#e6d89a] bg-[#fff4ba] ring-1 ring-[#e6d89a]"
                        : "border-[#dedbd2] bg-white hover:bg-[#f8f7f3]"
                    }`}
                  >
                    <span className="block text-sm font-semibold">
                      Estimate automatically
                    </span>
                    <span className="mt-1 block text-xs leading-4 text-[#77736a]">
                      BONI uses printed dimensions and known sizes in the image
                    </span>
                  </button>
                  <div
                    className="flex items-center gap-3 py-0.5"
                    aria-hidden="true"
                  >
                    <span className="h-px flex-1 bg-[#dedbd2]" />
                    <span className="text-[11px] font-semibold uppercase tracking-[0.14em] text-[#8a857a]">
                      or
                    </span>
                    <span className="h-px flex-1 bg-[#dedbd2]" />
                  </div>
                  <button
                    type="button"
                    onClick={() => setScaleMode("manual")}
                    className={`rounded-xl border p-3 text-left transition-colors ${
                      scaleMode === "manual"
                        ? "border-[#e6d89a] bg-[#fff4ba] ring-1 ring-[#e6d89a]"
                        : "border-[#dedbd2] bg-white hover:bg-[#f8f7f3]"
                    }`}
                  >
                    <span className="block text-sm font-semibold">
                      Set a reference measurement
                    </span>
                    <span className="mt-1 block text-xs leading-4 text-[#77736a]">
                      Mark a known distance in the image by setting two
                      reference points, then enter the actual length.
                    </span>
                  </button>
                </div>
                <div
                  role="note"
                  className="inline-flex max-w-full items-start gap-2 rounded-full border border-[#e8dca7] bg-[#fbf8e8] px-3 py-2 text-xs leading-4 text-[#3d3b37]"
                >
                  <HiSparkles
                    aria-hidden="true"
                    className="mt-0.5 h-4 w-4 shrink-0"
                  />
                  <span>
                    <strong>
                      {creditCostQuery.data?.floorplanImageImportCreditCost ===
                      undefined
                        ? "AI credits"
                        : `${creditCostQuery.data.floorplanImageImportCreditCost} AI credits`}
                    </strong>{" "}
                    are reserved when analysis starts and charged only if a
                    reviewable draft is produced.
                  </span>
                </div>
                <button
                  type="button"
                  disabled={
                    startState.loading ||
                    !file ||
                    (scaleMode === "manual" &&
                      (calibrationPoints.length !== 2 ||
                        !(Number(distanceMm) > 0)))
                  }
                  onClick={submit}
                  className="flex w-full items-center justify-center gap-2 rounded-lg bg-black px-4 py-3 text-sm font-semibold text-white hover:bg-[#292824] disabled:cursor-not-allowed disabled:opacity-45"
                >
                  <HiSparkles />{" "}
                  {startState.loading ? "Starting…" : "Scan and create draft"}
                </button>
              </div>
            ) : query.loading && !job ? (
              <div className="text-sm text-[#77736a]">Loading import…</div>
            ) : job?.draft && isReviewableStatus(job.status) ? (
              <div className="space-y-4">
                <div>
                  <div className="flex items-center gap-2 text-base font-semibold">
                    <span className="flex h-6 w-6 shrink-0 items-center justify-center rounded-full bg-emerald-100 text-emerald-700">
                      <FiCheck size={14} />
                    </span>
                    <span>
                      {reviewLocked
                        ? "Updating your drawing…"
                        : job.status === FloorplanImageImportStatus.Applied
                          ? alreadyInsertedInPlan
                            ? "Drawing inserted"
                            : "Scan saved"
                          : "Your drawing is ready"}
                    </span>
                  </div>
                  {reviewLocked ? (
                    <p className="mt-1 text-xs leading-4 text-[#69655d]">
                      Hang tight while BONI refreshes the traced draft.
                    </p>
                  ) : job.status === FloorplanImageImportStatus.Applied ? (
                    <p className="mt-1 text-xs leading-4 text-[#69655d]">
                      {alreadyInsertedInPlan
                        ? "You can insert another copy if you need a second version."
                        : "Open this scan anytime to insert it into the workspace."}
                    </p>
                  ) : FLOORPLAN_IMPORT_REVIEW_EXTRA_CONTROLS_ENABLED ? (
                    <p className="mt-1 text-xs leading-4 text-[#69655d]">
                      {selectedInsertableCount} of {candidates.length} selected
                    </p>
                  ) : (
                    <p className="mt-1 text-xs leading-4 text-[#69655d]">
                      {structureCandidateCount}{" "}
                      {structureCandidateCount === 1
                        ? "structure detected"
                        : "structures detected"}
                    </p>
                  )}
                  {!reviewLocked && (
                      <div className="mt-2 flex flex-wrap items-center gap-x-4 gap-y-1.5">
                        {labelRefs.length > 0 && (
                          <label
                            className="flex cursor-pointer items-center gap-2 text-xs font-medium"
                            title="Optional: insert detected room labels with the drawing"
                          >
                            <input
                              type="checkbox"
                              checked={includeTextAnnotations}
                              onChange={(event) =>
                                setIncludeTextAnnotations(event.target.checked)
                              }
                            />
                            Add text
                          </label>
                        )}
                        <label
                          className="flex cursor-pointer items-center gap-2 text-xs font-medium"
                          title="Show or hide the detected structure overlay on the source image"
                        >
                          <input
                            type="checkbox"
                            checked={overlayVisible}
                            onChange={(event) =>
                              setOverlayVisible(event.target.checked)
                            }
                          />
                          Markers
                        </label>
                      </div>
                    )}
                </div>
                {FLOORPLAN_IMPORT_REVIEW_EXTRA_CONTROLS_ENABLED && (
                <div className="rounded-xl border border-[#d9d5cb] bg-white p-3">
                  <div className="flex items-start justify-between gap-3">
                    <div className="min-w-0">
                      <p className="text-sm font-semibold text-[#171713]">
                        Scale
                      </p>
                      <p className="mt-1 text-xs text-[#69655d]">
                        {scaleSource === FloorplanImportScaleSource.Manual
                          ? "Applied from your reference measurement"
                          : scaleSource ===
                              FloorplanImportScaleSource.VisibleMeasurements
                            ? "Applied from dimensions found in the drawing"
                            : "Estimated automatically during the scan"}
                      </p>
                      <p className="mt-1 text-xs leading-4 text-[#69655d]">
                        Dimensions are easiest to verify after insert on a known
                        wall. Use Adjust scale here only if the overall size
                        looks clearly wrong — you will mark two points and enter
                        a real length, same as before scanning.
                      </p>
                      {scaleOverrideOpen && Number(distanceMm) > 0 && (
                        <p className="mt-2 text-xs font-medium text-[#171713]">
                          New reference: {distanceMm} mm (applied when you tap
                          Use this scale)
                        </p>
                      )}
                    </div>
                    {!scaleOverrideOpen && (
                      <button
                        type="button"
                        onClick={beginScaleOverride}
                        disabled={reviewLocked}
                        className="shrink-0 rounded-lg border border-[#bbb6a9] bg-white px-3 py-1.5 text-xs font-semibold hover:bg-[#f0eee7] focus:outline-none focus-visible:ring-2 focus-visible:ring-black disabled:cursor-not-allowed disabled:opacity-45"
                      >
                        Adjust scale
                      </button>
                    )}
                  </div>
                  {scaleOverrideOpen && (
                    <div className="mt-3 border-t border-[#e4e1d9] pt-3">
                      <p className="text-xs leading-4 text-[#555149]">
                        Mark a known distance in the image by setting two
                        reference points, then enter the actual length. Drag the
                        points to adjust. Hold Shift to move the line freely
                        without snapping to 45-degree angles. Alt-drag or
                        middle-mouse to pan.
                      </p>
                      <p className="mt-2 text-xs text-[#69655d]">
                        {calibrationPoints.length} of 2 points marked
                      </p>
                      <div className="mt-3 flex gap-2">
                        <button
                          type="button"
                          onClick={saveScaleOverride}
                          disabled={
                            reviewLocked ||
                            calibrationState.loading ||
                            calibrationPoints.length !== 2 ||
                            !(Number(distanceMm) > 0)
                          }
                          className="flex-1 rounded-lg bg-black px-3 py-2 text-xs font-semibold text-white hover:bg-[#292824] disabled:cursor-not-allowed disabled:opacity-45"
                        >
                          {calibrationState.loading
                            ? "Saving…"
                            : "Use this scale"}
                        </button>
                        <button
                          type="button"
                          onClick={cancelScaleOverride}
                          disabled={calibrationState.loading}
                          className="rounded-lg border border-[#bbb6a9] bg-white px-3 py-2 text-xs font-semibold hover:bg-[#f0eee7] disabled:opacity-45"
                        >
                          Cancel
                        </button>
                      </div>
                    </div>
                  )}
                </div>
                )}
                {unavailableLayers.length > 0 && (
                  <div className="space-y-2">
                    {unavailableLayers.map((layer) => (
                      <div
                        key={layer.stage}
                        className="flex items-center justify-between gap-3 rounded-xl bg-[#ece9e1] px-3 py-2.5"
                      >
                        <div>
                          <div className="text-xs font-semibold text-[#3f3c36]">
                            {layer.label} not checked
                          </div>
                          <div className="mt-0.5 text-[11px] text-[#69655d]">
                            You can insert the rest or try this part again.
                          </div>
                        </div>
                        <button
                          type="button"
                          disabled={
                            reviewLocked ||
                            job.status === FloorplanImageImportStatus.Applied ||
                            retryStageState.loading
                          }
                          onClick={() => retryLayer(layer.stage)}
                          className="shrink-0 rounded-lg border border-[#aaa598] bg-white px-2.5 py-1.5 text-[11px] font-semibold hover:bg-[#f8f7f3] focus:outline-none focus-visible:ring-2 focus-visible:ring-black disabled:opacity-45"
                        >
                          Try again
                        </button>
                      </div>
                    ))}
                  </div>
                )}
                {FLOORPLAN_IMPORT_REVIEW_EXTRA_CONTROLS_ENABLED && (
                <>
                <p className="text-xs leading-4 text-[#69655d]">
                  Uncheck anything you don’t want to insert. Labels are off by
                  default — most edits are easier in the workspace after insert.
                </p>
                <div className="max-h-[42vh] space-y-2 overflow-y-auto pr-1">
                  {(
                    ["structure", "symbols", "annotations"] as ReviewRoot[]
                  ).map((root) => {
                    const refs = rootRefs(root);
                    if (!refs.length) return null;
                    const rootState = getReviewSelectionState(
                      refs,
                      excludedRefs,
                    );
                    const subgroups = Array.from(
                      new Set(
                        candidates
                          .filter((candidate) => candidate.root === root)
                          .map((candidate) => candidate.subgroup),
                      ),
                    );
                    return (
                      <details
                        key={root}
                        className="group overflow-hidden rounded-xl border border-[#d9d5cb] bg-white"
                      >
                        <summary className="flex cursor-pointer list-none items-center gap-2 px-3 py-3 focus:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-black">
                          <TriStateCheckbox
                            state={rootState}
                            label={`Include all ${rootLabels[root]}`}
                            disabled={reviewLocked}
                            onChange={() =>
                              setCandidatesIncluded(
                                refs,
                                rootState !== "checked",
                              )
                            }
                          />
                          <span className="min-w-0 flex-1 text-sm font-semibold text-[#171713]">
                            {rootLabels[root]}
                          </span>
                          <span className="text-[11px] tabular-nums text-[#69655d]">
                            {
                              refs.filter((ref) => !excludedRefs.has(ref))
                                .length
                            }
                            /{refs.length}
                          </span>
                          <FiChevronDown
                            size={14}
                            className="text-[#69655d] transition-transform group-open:rotate-180 motion-reduce:transition-none"
                          />
                        </summary>
                        <div className="border-t border-[#e5e2da] bg-[#f8f7f3] px-2 pb-2">
                          {subgroups.map((subgroup) => {
                            const subgroupCandidates = candidates.filter(
                              (candidate) =>
                                candidate.root === root &&
                                candidate.subgroup === subgroup,
                            );
                            const subgroupRefs = subgroupCandidates.map(
                              (candidate) => candidate.ref,
                            );
                            const subgroupState = getReviewSelectionState(
                              subgroupRefs,
                              excludedRefs,
                            );
                            return (
                              <div key={`${root}-${subgroup}`} className="pt-2">
                                <div className="mb-1.5 flex items-center gap-2 px-1">
                                  <TriStateCheckbox
                                    state={subgroupState}
                                    label={`Include all ${subgroup}`}
                                    disabled={reviewLocked}
                                    onChange={() =>
                                      setCandidatesIncluded(
                                        subgroupRefs,
                                        subgroupState !== "checked",
                                      )
                                    }
                                  />
                                  <span className="text-[11px] font-semibold text-[#555149]">
                                    {subgroup}
                                  </span>
                                  <span className="ml-auto text-[10px] tabular-nums text-[#77736a]">
                                    {subgroupRefs.length}
                                  </span>
                                </div>
                                <div className="space-y-1.5">
                                  {subgroupCandidates.map((candidate) => {
                                    const symbol = job.draft?.symbols.find(
                                      (item) => item.ref === candidate.ref,
                                    );
                                    const override = symbolOverrides.get(
                                      candidate.ref,
                                    );
                                    const libraryKey =
                                      override?.libraryKey ||
                                      symbol?.libraryKey;
                                    const catalogEntry = libraryKey
                                      ? catalogByKey.get(libraryKey)
                                      : undefined;
                                    const rotation =
                                      override?.rotation ??
                                      (symbol && catalogEntry
                                        ? resolveImportedCatalogRotation({
                                            symbol,
                                            assetWidth: catalogEntry.width,
                                            assetHeight: catalogEntry.height,
                                          })
                                        : (symbol?.rotation ?? 0));
                                    return (
                                      <div key={candidate.ref}>
                                        <CandidateRow
                                          entityRef={candidate.ref}
                                          label={candidate.label}
                                          kind={candidate.kind}
                                          markerNumber={
                                            candidateNumbers.get(
                                              candidate.ref,
                                            ) || 1
                                          }
                                          excluded={excludedRefs.has(
                                            candidate.ref,
                                          )}
                                          active={activeRef === candidate.ref}
                                          unavailable={!candidate.insertable}
                                          disabled={reviewLocked}
                                          onToggle={() =>
                                            toggleExcluded(candidate.ref)
                                          }
                                          onActivate={() => {
                                            setActiveRef(candidate.ref);
                                            setOverlayVisible(true);
                                          }}
                                          onHover={(hovered) =>
                                            setHoveredRef(
                                              hovered
                                                ? candidate.ref
                                                : undefined,
                                            )
                                          }
                                        />
                                        {symbol &&
                                          activeRef === candidate.ref && (
                                            <SymbolOverrideControls
                                              catalog={symbolCatalog}
                                              libraryKey={libraryKey}
                                              rotation={rotation}
                                              disabled={reviewLocked}
                                              onChange={(next) => {
                                                const nextEntry =
                                                  catalogByKey.get(
                                                    next.libraryKey,
                                                  );
                                                setSymbolOverride(
                                                  symbol.ref,
                                                  next.libraryKey !==
                                                    libraryKey && nextEntry
                                                    ? {
                                                        ...next,
                                                        rotation:
                                                          resolveImportedCatalogRotation(
                                                            {
                                                              symbol,
                                                              assetWidth:
                                                                nextEntry.width,
                                                              assetHeight:
                                                                nextEntry.height,
                                                            },
                                                          ),
                                                      }
                                                    : next,
                                                );
                                              }}
                                            />
                                          )}
                                      </div>
                                    );
                                  })}
                                </div>
                              </div>
                            );
                          })}
                        </div>
                      </details>
                    );
                  })}
                </div>
                </>
                )}
                <button
                  type="button"
                  disabled={
                    reviewLocked ||
                    markState.loading ||
                    updateReviewState.loading ||
                    selectedInsertableCount <= 0 ||
                    floorplannerStore.lockedByOther ||
                    floorplannerStore.collabAccessLost
                  }
                  onClick={apply}
                  className="flex w-full items-center justify-center gap-2 rounded-lg bg-black px-4 py-3 text-sm font-semibold text-white hover:bg-[#292824] disabled:opacity-50"
                >
                  {applyingDraft ? (
                    <>
                      <FiRefreshCw className="animate-spin motion-reduce:animate-none" />
                      Inserting drawing…
                    </>
                  ) : (
                    <>
                      <FiCheck />
                      {alreadyInsertedInPlan
                        ? "Insert another copy"
                        : FLOORPLAN_IMPORT_REVIEW_EXTRA_CONTROLS_ENABLED
                          ? "Insert selected draft"
                          : "Insert into workspace"}
                    </>
                  )}
                </button>
                <button
                  type="button"
                  onClick={cancel}
                  disabled={cancelState.loading}
                  className="w-full py-2 text-xs font-medium text-red-700 underline underline-offset-2"
                >
                  Discard scan
                </button>
              </div>
            ) : job?.status === FloorplanImageImportStatus.Failed ? (
              <div className="space-y-4">
                <h3 className="text-base font-semibold">
                  The image could not be reconstructed
                </h3>
                <p className="rounded-lg bg-red-50 p-3 text-sm leading-5 text-red-800">
                  We couldn’t trace this drawing. Try again; a clearer or
                  higher-resolution image may help.
                </p>
                <button
                  type="button"
                  onClick={retry}
                  disabled={retryState.loading}
                  className="flex w-full items-center justify-center gap-2 rounded-lg bg-black px-4 py-3 text-sm font-semibold text-white disabled:opacity-50"
                >
                  <FiRefreshCw /> Retry analysis
                </button>
                <button
                  type="button"
                  onClick={cancel}
                  disabled={cancelState.loading}
                  className="w-full py-2 text-xs font-medium text-red-700 underline underline-offset-2"
                >
                  Discard import
                </button>
              </div>
            ) : job?.status === FloorplanImageImportStatus.Cancelling ? (
              <div className="space-y-4">
                <h3 className="text-base font-semibold">
                  Finishing cancellation
                </h3>
                <p className="rounded-lg bg-amber-50 p-3 text-sm leading-5 text-amber-900">
                  The drawing source is still being removed. Retry cleanup to
                  finish safely.
                </p>
                <button
                  type="button"
                  onClick={cancel}
                  disabled={cancelState.loading}
                  className="flex w-full items-center justify-center gap-2 rounded-lg bg-black px-4 py-3 text-sm font-semibold text-white disabled:opacity-50"
                >
                  <FiRefreshCw /> Retry cleanup
                </button>
              </div>
            ) : job ? (
              <div className="space-y-5">
                <div>
                  <div className="text-xs font-semibold uppercase tracking-[0.12em] text-[#77736a]">
                    Analysis progress
                  </div>
                  <h3 className="mt-2 text-base font-semibold">
                    {stageLabel(job.stage)}
                  </h3>
                  <p className="mt-1 text-sm text-[#69655d]">
                    BONI is building a measured, editable draft. Review options
                    appear when analysis reaches 100%.
                  </p>
                </div>
                <ol className="space-y-3">
                  {(job.pipelineVersion === "floorplan-image-v5"
                    ? stageLabels
                    : legacyStageLabels
                  ).map(([value, label], index, stages) => {
                    const state = job.stageStates.find(
                      (item) => item.stage === value,
                    );
                    const currentIndex = stages.findIndex(
                      ([item]) => item === job.stage,
                    );
                    const complete =
                      state?.status === FloorplanImportStageStatus.Complete ||
                      (!state && index < currentIndex);
                    const current =
                      state?.status === FloorplanImportStageStatus.Running ||
                      value === job.stage;
                    return (
                      <li
                        key={value}
                        className="flex items-center gap-3 text-sm"
                      >
                        <span
                          className={`flex h-6 w-6 items-center justify-center rounded-full border ${complete ? "border-emerald-600 bg-emerald-600 text-white" : current ? "border-[#ad8500] bg-[#f5cb2e]" : "border-[#c9c5ba] bg-white text-[#8b877f]"}`}
                        >
                          {complete ? <FiCheck size={13} /> : index + 1}
                        </span>
                        <span className="min-w-0 flex-1">
                          <span
                            className={
                              current ? "font-semibold" : "text-[#69655d]"
                            }
                          >
                            {label}
                          </span>
                          {state && state.entityCount > 0 && (
                            <span className="ml-2 text-[11px] tabular-nums text-[#77736a]">
                              {state.entityCount} detected
                            </span>
                          )}
                        </span>
                      </li>
                    );
                  })}
                </ol>
                <button
                  type="button"
                  onClick={cancel}
                  disabled={cancelState.loading}
                  className="w-full rounded-lg border border-[#c9c5ba] bg-white px-4 py-2.5 text-sm font-medium hover:bg-[#f0eee7] disabled:opacity-50"
                >
                  Cancel import
                </button>
              </div>
            ) : (
              <div className="text-sm text-red-700">
                This import could not be loaded.
              </div>
            )}

            {error && (
              <div
                role="alert"
                className="mt-4 rounded-lg border border-red-200 bg-red-50 p-3 text-sm leading-5 text-red-800"
              >
                {error}
              </div>
            )}
            {warning && (
              <div
                role="status"
                className="mt-4 rounded-lg border border-amber-200 bg-amber-50 p-3 text-sm leading-5 text-amber-900"
              >
                {warning}
              </div>
            )}
          </aside>
        </div>
      </div>
    </div>,
    document.body,
  );
};
